<template>
  <div style="clear: both">
    <v-overlay v-model="loading" class="align-center justify-center">
      <v-card dark width="300">
        <v-card-text>
          {{ $t('Loading') }}
          <v-progress-linear indeterminate color="light-green-darken-4" striped height="10" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-overlay>

    <template v-if="formReady">
      <div class="moc-form">
        <v-container fluid>
          <template v-if="flagsMessage">
            <flags-notifier :message="flagsMessage" :color="flagsMessageColor"></flags-notifier>
          </template>
          
          <v-form v-model="valid" ref="mocForm" autocomplete="off">
            <v-card :color="formColor" class="px-3">
              <v-row justify="end" dense v-if="moc.status.value != newStatus">
                <v-col cols="12" sm="12">
                  <pre class="viewers">{{ $t('Being_Viewed_By') }}: {{ displayViewingUsers }}</pre>
                </v-col>
              </v-row>
              <v-row dense style="padding-top: 8px">
                <v-col cols="12" lg="2">
                  <initiated-date-field :date="$filters.DateFilter(moc.dates.initiated)" :readonly="true"></initiated-date-field>
                </v-col>

                <v-col cols="12" lg="4">
                  <community-field v-model="moc.community" :readonly="true"></community-field>
                </v-col>

                <v-col cols="12" lg="2">
                  <id-field v-model="moc.mocId" :readonly="true"></id-field>
                </v-col>

                <v-col cols="12" lg="4">
                  <status-field :model-value="moc.status"></status-field>
                </v-col>
              </v-row>

              <v-row dense v-if="moc.status.value == closedVoidedStatus && moc.closedVoidedReason" class="pb-3 mt-n4">
                <v-col cols="12" sm="12" class="white">
                  <void-reason
                    :closedVoidedReason="moc.closedVoidedReason"
                    :mocFormStatus="moc.status.value"
                    :closedVoidUndo="moc.closedVoidUndo"
                  ></void-reason>
                </v-col>
              </v-row>
              <v-row dense v-if="moc.status.value == closedStrandedStatus && moc.closedStrandedReason" class="pb-3 mt-n4">
                <v-col cols="12" sm="12" class="white">
                  <stranded-reason
                    :closedStrandedReason="moc.closedStrandedReason"
                    :mocFormStatus="moc.status.value"
                  ></stranded-reason>
                </v-col>
              </v-row>
              <v-row
                dense
                v-if="moc.status.value === sentBackStatus && moc.sentBackReason && displaySentBackReadOnly"
                class="pb-3 mt-n4"
              >
                <v-col cols="12" sm="12" class="white">
                  <sent-back-reason
                    :sentBackReason="moc.sentBackReason"
                    :sentBackHistory="moc.sentBackHistory"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                  ></sent-back-reason>
                </v-col>
              </v-row>

              <v-row dense v-if="moc.status.value === beingRevisedStatus && moc.revisionReason" class="pb-3 mt-n4">
                <v-col cols="12" sm="12" class="white">
                  <being-revised-reason
                    :revisionReason="moc.revisionReason"
                    :revisionHistory="moc.revisionHistory"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                  ></being-revised-reason>
                </v-col>
              </v-row>

              <v-row
                dense
                v-if="
                  (moc.status.value === changeIsInPlaceWithConsequentialActionsRequired && moc.closerDeniedReason) ||
                  (moc.status.value === temporaryChangeIsInPlaceWithConsequentialActionsRequired && moc.closerDeniedReason)
                "
                class="pb-3 mt-n4"
              >
                <v-col cols="12" sm="12" class="white">
                  <closer-denied-reason
                    :closerDeniedReason="moc.closerDeniedReason"
                    :closerDeniedHistory="moc.closerDeniedHistory"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                  ></closer-denied-reason>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" sm="6" lg="6">
                  <people-picker
                    v-model="moc.roles.initiator"
                    :label="$t('Initiator')"
                    :user="initiator"
                    name="initiator"
                    :readonly="true"
                    v-on:clear="
                      () => {
                        moc.roles.initiator = null
                        checked = true
                      }
                    "
                    v-on:blur="initiatorChecked = true"
                    :checked="initiatorChecked"
                    required
                    :requiredText="$t('Rule_Required_Initiator')"
                    v-on:update:model-value="handleInitiatorsChange()"
                  ></people-picker>
                </v-col>
                <v-col cols="12" sm="6" lg="6">
                  <people-picker
                    v-model="moc.roles.coinitiator"
                    v-on:clear="moc.roles.coinitiator = null"
                    :label="$t(readonly ? 'Co-Initiator' : 'Co-Initiator_Readonly')"
                    :user="moc.roles.coinitiator"
                    :readonly="readonly"
                    v-on:update:model-value="handleInitiatorsChange()"
                    ref="coinitiator"
                  ></people-picker>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <title-field
                    v-model="moc.title"
                    :model-value="moc.title"
                    :roles="myRoles"
                    :previousFormStatus="previousFormStatus"
                    :moc="moc"
                    ref="title"
                  ></title-field>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <change-category-field
                    v-model="moc.changeCategory"
                    :model-value="moc.changeCategory"
                    :items="changeCategories"
                    v-on:change="handleChangeCategoryChanged()"
                    :roles="myRoles"
                    :previousFormStatus="previousFormStatus"
                    :moc="moc"
                  ></change-category-field>
                </v-col>
              </v-row>

              <div v-show="moc.changeCategory != null">
                <v-row dense>
                  <v-col cols="12">
                    <type-field
                      v-model="moc.mocType"
                      :model-value="moc.mocType"
                      :items="mocTypes"
                      :roles="myRoles"
                      v-on:change="handleMocTypeChange()"
                      :previousFormStatus="previousFormStatus"
                      :moc="moc"
                    ></type-field>
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <description-field
                      v-model="moc.description"
                      :model-value="moc.description"
                      :roles="myRoles"
                      :previousFormStatus="previousFormStatus"
                      :moc="moc"
                    ></description-field>
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <justification-field
                      v-model="moc.justification"
                      :model-value="moc.justification"
                      :roles="myRoles"
                      :previousFormStatus="previousFormStatus"
                      :moc="moc"
                    ></justification-field>
                  </v-col>
                </v-row>

                <v-row dense>
                  <template v-if="!moc.dates.approvedStart">
                    <v-col cols="12" sm="4">
                      
                      <start-date-field
                        
                        v-model="moc.dates.start"
                        :roles="myRoles"
                        :moc="moc"
                        :previousFormStatus="previousFormStatus"
                        :changeType="moc.changeType.type"
                      ></start-date-field>
                    </v-col>
                  </template>

                  <template v-if="moc.dates.approvedStart">
                    <v-col cols="12" sm="4">
                      <approved-start-date-field
                        v-model="moc.dates.approvedStart"
                        :roles="myRoles"
                        :moc="moc"
                        forceReadonly
                      ></approved-start-date-field>
                    </v-col>
                  </template>

                  <v-col cols="12" sm="4">                   
                    <change-type-field
                      v-model="moc.changeType"
                      :items="changeTypes"
                      :roles="myRoles"
                      v-on:change="handleChangeTypeChanged($event)"
                      
                      :previousFormStatus="previousFormStatus"
                      :moc="moc"
                    ></change-type-field>
                  </v-col>

                  <template v-if="selectedChangeTypeKey === temporaryChangeTypeKey">
                    <v-col cols="12" sm="4">
                      <valid-until-date-field
                        v-model="moc.dates.validUntil"
                        
                        :roles="myRoles"
                        :mocFormStatus="moc.status.value"
                        :changeType="moc.changeType.type"
                      ></valid-until-date-field>
                    </v-col>
                  </template>
                </v-row>
                <template v-if="currentQuestions">
                  <v-row dense>
                    <v-col cols="12">
                      <tree-questions
                        :questions="currentQuestions"
                        :roles="myRoles"
                        @answered="handleQuestionAnswered($event)"
                        @dirty="handleQuestionsDirty($event)"
                        :previousFormStatus="previousFormStatus"
                        :moc="moc"
                      ></tree-questions>
                    </v-col>
                  </v-row>
                </template>

                <template v-if="!isClosed">
                  <v-row dense>
                    <v-col cols="11">
                      <v-btn
                        class="mb-2 mr-2"
                        :disabled="saveContinueDisabled"
                        @click="saveAndContinueClick()"
                        v-show="!readonly"
                        >{{ $t('Save_Continue') }}</v-btn
                      >
                    </v-col>
                  </v-row>
                </template>

                <template v-if="moc.revisionHistory.length > 0">
                  <v-row justify="end" dense v-if="moc.status.value != newStatus">
                    <v-col cols="12" sm="12">
                      <pre class="revision">{{ $t('Revision') }}: {{ moc.revisionHistory.length }}</pre>
                    </v-col>
                  </v-row>
                </template>
              </div>
            </v-card>
          </v-form>
          <div v-show="moc.changeCategory != null">
            <v-row dense class="mt-3">
              <v-col cols="12">
                <supporting-documents :moc="moc" @onDocumentsLoad="loadSupportingDocuments" :roles="myRoles" :showOpenText="openTextEnabled">
                </supporting-documents>
              </v-col>
            </v-row>
            <v-row dense class="mt-3">
              <v-col cols="12">
                <supporting-links :moc="moc" @onLinksLoad="loadSupportingLinks" :roles="myRoles"></supporting-links>
              </v-col>
            </v-row>

            <v-row dense class="mt-3">
              <v-col cols="12">
                <v-card>
                  <all-tasks
                    :moc="moc"
                    :tasks="tasks"
                    :roles="myRoles"
                    :readonly="!saveContinueDisabled || (isMultipass && isMultipassGtZero)"
                    @taskStatusChange="areTasksCompleted()"
                    @loadFilteredTasksInCurrentStatus="getTasksForCurrentStatus"
                    :previousFormStatus="previousFormStatus"
                    :show="showTasks"
                  ></all-tasks>
                </v-card>
              </v-col>
            </v-row>

            <v-card :class="faBackgroundColor">
              <div class="mt-3">
                <v-container fluid>
                  <template v-if="moc.multiPassCurrentPass === 0">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <people-picker-ms
                          :label="$t('Preset_Approvers')"
                          v-model="moc.roles.approvers"
                          :title="$t('Preset_Approvers')"
                          :readonly="true"
                          @update="(args) => this.handleUpdateApprovers(args)"
                          :showCounter="true"
                          ref="PresetApproversPeoplePicker"
                          :color="faComponentColor"
                          showRequired
                          showRequiredReadOnly
                        ></people-picker-ms>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <people-picker-ms
                          :label="$t('Preset_FYIs')"
                          v-model="moc.roles.fyis"
                          :title="$t('Preset_FYIs')"
                          :readonly="true"
                          @update="(args) => this.handleUpdateFyis(args)"
                          :showCounter="true"
                          ref="PresetFyisPeoplePicker"
                          :color="faComponentColor"
                        ></people-picker-ms>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" sm="6">
                        <people-picker-ms
                          :label="$t('Additional_Approvers')"
                          v-model="moc.roles.additionalApprovers"
                          :title="$t('Additional_Approvers')"
                          :readonly="readonlyAdditionalApprovers"
                          @update="(args) => this.handleUpdateAdditionalApprovers(args)"
                          @beforeDelete="handleDeleteAdditionalApprovers"
                          ref="AdditionalApproversPeoplePicker"
                          :color="faComponentColor"
                          :showExternalSelection="!isCommunityConfidential"
                          showRequired
                          :showRequiredReadOnly="additionalApproversShowRequiredReadOnly"
                          :bulkUserAdd="!readonlyAdditionalApprovers"
                        ></people-picker-ms>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <people-picker-ms
                          :label="$t('Additional_FYI')"
                          v-model="moc.roles.additionalFyis"
                          :title="$t('Additional_FYI')"
                          :readonly="readonlyAdditionalFyis"
                          @update="(args) => this.handleUpdateAdditionalFyis(args)"
                          ref="AdditionalFyisPeoplePicker"
                          :color="faComponentColor"
                          :showExternalSelection="!isCommunityConfidential"
                          :bulkUserAdd="!readonlyAdditionalFyis"
                        ></people-picker-ms>
                      </v-col>
                    </v-row>
                  </template>

                  <template v-else>
                    <v-row>
                      <v-expansion-panels accordion class="mx-3">
                        <v-expansion-panel>
                          <v-expansion-panel-title
                            disable-icon-rotate
                            @click="showOriginalDecision = !showOriginalDecision"
                            :color="faComponentColor"
                            >{{ $t('Original_Decision') }}
                            <template v-slot:actions>
                              <v-icon>{{ showOriginalDecision ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                            </template>
                          </v-expansion-panel-title>
                          <v-expansion-panel-text :class="faBackgroundColor">
                            <v-row>
                              <v-col cols="12" sm="6">
                                <people-picker-ms
                                  :label="$t('Preset_Approvers')"
                                  v-model="moc.roles.approvers"
                                  :title="$t('Preset_Approvers')"
                                  :readonly="true"
                                  @update="(args) => this.handleUpdateApprovers(args)"
                                  :showCounter="true"
                                  ref="PresetApproversPeoplePicker"
                                  :color="faComponentColor"
                                ></people-picker-ms>
                              </v-col>

                              <v-col cols="12" sm="6">
                                <people-picker-ms
                                  :label="$t('Preset_FYIs')"
                                  v-model="moc.roles.fyis"
                                  :title="$t('Preset_FYIs')"
                                  :readonly="true"
                                  @update="(args) => this.handleUpdateFyis(args)"
                                  :showCounter="true"
                                  ref="PresetFyisPeoplePicker"
                                  :color="faComponentColor"
                                ></people-picker-ms>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col cols="12" sm="6">
                                <people-picker-ms
                                  :label="$t('Additional_Approvers')"
                                  v-model="moc.roles.additionalApprovers"
                                  :title="$t('Additional_Approvers')"
                                  :readonly="true"
                                  @update="(args) => this.handleUpdateAdditionalApprovers(args)"
                                  @beforeDelete="handleDeleteAdditionalApprovers"
                                  ref="AdditionalApproversPeoplePicker"
                                  :color="faComponentColor"
                                  :showExternalSelection="!isCommunityConfidential"
                                  showRequired
                                ></people-picker-ms>
                              </v-col>

                              <v-col cols="12" sm="6">
                                <people-picker-ms
                                  :label="$t('Additional_FYI')"
                                  v-model="moc.roles.additionalFyis"
                                  :title="$t('Additional_FYI')"
                                  :readonly="true"
                                  @update="(args) => this.handleUpdateAdditionalFyis(args)"
                                  ref="AdditionalFyisPeoplePicker"
                                  :color="faComponentColor"
                                  :showExternalSelection="!isCommunityConfidential"
                                ></people-picker-ms>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-row>
                  </template>
                </v-container>
                <!-- multipass, lets display the approvers for the current and past passes -->
                <template v-if="displayMultipassApprovers">
                  <multipass-approvers
                    :moc="moc"
                    :isCommunityConfidential="isCommunityConfidential"
                    :forceReadonly="readonlyAdditionalApprovers"
                  ></multipass-approvers>
                </template>
                <!-- / multipass approvers -->
              </div>
            </v-card>

            <!-- actions for facilitator -->
            <v-row dense class="mt-3">
              <v-col cols="12">
                <facilitator-actions
                  :approvalProcessType="moc.approvalProcessType"
                  :approvalProcessTypes="approvalProcessTypes"
                  :roles="myRoles"
                  :mocFormStatus="moc.status.value"
                  :moc="moc"
                  :mocOwner="moc.roles.owner"
                  :areTasksCompleted="allTasksComplete"
                  :tasks="tasks"
                  :tempAdditionalApprovers="tempAdditionalApprovers"
                  @selectedOwner="setOwner($event)"
                  @selectedAPT="approvalProcessTypeChanged"
                  @selectedCoordinator="setCoordinator"
                  @selectedFacilitator="setFacilitator"
                  @selectedFacilitatorBackup="setFacilitatorBackup"
                ></facilitator-actions>
              </v-col>
            </v-row>

            <v-row dense class="mt-3">
              <v-col cols="12" sm="10">
                <processing-comments-field
                  v-model="moc.additionalInformation"
                  
                  :roles="myRoles"
                  :mocFormStatus="moc.status.value"
                  :previousFormStatus="previousFormStatus"
                ></processing-comments-field>
              </v-col>
            </v-row>

            <v-row
              dense
              class="mt-3"
              v-if="
                moc.status.value == submittedStatus &&
                moc.sentBackHistory &&
                moc.sentBackHistory.length > 0 &&
                displayFacilitatorActions
              "
            >
              <v-col cols="12" sm="10">
                <sent-back-reason
                  :sentBackReason="lastSentBackHistoryItem"
                  :sentBackHistory="moc.sentBackHistory"
                  :roles="myRoles"
                  :mocFormStatus="moc.status.value"
                ></sent-back-reason>
              </v-col>
            </v-row>

            <!-- approvals --->
            <v-row dense v-if="displayApprovals" class="mt-3">
              <v-col cols="12">
                <approvals
                  :moc="moc"
                  :setApprovals="setMocApprovals"
                  :showHistory="
                    displayApprovals || (moc.flags || []).includes('unauthorized') || (moc.flags || []).includes('multipass')
                  "
                  :bgColor="faBackgroundColor"
                  ref="approvals"
                ></approvals>
              </v-col>
            </v-row>

            <!-- This is just a placeholder for discussion, not sure where the fire is to have to add this now -->
            <v-row dense v-if="displayDiscussion" class="mt-3">
              <v-col cols="12">
                <comments
                  :readonly="readonlyDiscussion"
                  :mocId="moc.mocId"
                  :showToast="toaster.pop"
                  :initialDiscussionDisplay="initialDiscussionDisplay"
                ></comments>
              </v-col>
            </v-row>

            <!-- owner decision -->
            <v-row dense v-if="moc.ownerDecision && (moc.ownerDecision.decision || {}).key && !isBeingRevised" class="mt-3">
              <v-col cols="12">
                <owner-decision :ownerDecision="moc.ownerDecision" :moc="moc"></owner-decision>
              </v-col>
            </v-row>

            <!-- owner actions -->
            <v-row dense v-if="displayOwnerActions" class="mt-3">
              <v-col cols="12">
                <owner-actions
                  :roles="myRoles"
                  :moc="moc"
                  :tempAdditionalApprovers="tempAdditionalApprovers"
                  :tempAdditionalFyis="tempAdditionalFyis"
                  :areTasksCompleted="allTasksComplete"
                  @selectedCoordinator="setCoordinator"
                  :tasks="tasks"
                  :showTempActions="temporaryActionsInfoPanelShow"
                ></owner-actions>
              </v-col>
            </v-row>

            <!-- initiator actions -->
            <v-row dense v-if="displayInitiatorActions" class="mt-3">
              <v-col cols="12">
                <initiator-actions :roles="myRoles" :mocFormStatus="moc.status.value" :moc="moc"></initiator-actions>
              </v-col>
            </v-row>

            <!-- Coordinator Actions -->
            <v-row dense v-if="displayCoordinatorActions" class="mt-3">
              <v-col cols="12">
                <coordinator-actions :moc="moc" :tasks="tasks" class="moc-secondary"></coordinator-actions>
              </v-col>
            </v-row>

            <div v-if="displayHistory">
              <v-row dense class="mt-3">
                <v-col>
                  <history :moc="moc" />
                </v-col>
              </v-row>
            </div>

            <!-- Save, Save Draft, Cancel buttons -->
            <v-row dense class="mt-3">
              <template v-if="activateSaveControls()">
                <v-btn v-if="isInitiator && !isSentBack" color="accent" @click="saveDraft()" class="mx-2"><span style="color: white !important;">{{
                  $t('Save_Draft')}}</span>
                </v-btn>
                <v-btn
                  v-if="isInitiator && isSentBack"
                  color="accent"
                  @click="saveSentBack()"
                  :disabled="!saveEnabled"
                  class="mx-2"
                  ><span style="color: white !important;">{{ $t('Save') }}</span></v-btn
                >
                <v-btn
                  v-if="isInitiator && (isSentBack || isDraft || isNew || isBeingRevised)"
                  color="success"
                  @click="submit()"
                  :disabled="!fullValid"
                  class="mx-2"
                  >{{ $t('Submit') }}</v-btn
                >
              </template>

              <v-dialog v-model="formSettings.cancelDialog" persistent max-width="290">
                <template v-slot:activator="{ props: activatorProps }">
                  <template v-if="isClosed">
                    <v-btn color="info" @click="cancelled()" class="mx-2">{{ $t('Exit') }}</v-btn>
                  </template>
                  <template v-else>
                    <v-btn color="info" v-bind="activatorProps" class="mx-2">{{ $t('Exit_Without_Saving') }}</v-btn>
                  </template>
                </template>
                <v-card>
                  <v-toolbar color="warning" dense>
                    <v-toolbar-title></v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-icon class="me-3" color="black">mdi-bell-alert-outline</v-icon>
                  </v-toolbar>
                  <v-card-text class="text-h6 font-weight-bold">{{ $t('Confirm_ExitWithoutSaving') }}</v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="info" text class="card-button" @click="formSettings.cancelDialog = false">{{ $t('No') }}</v-btn>
                    <v-btn color="error" text class="card-button" @click="cancelled()">{{ $t('Yes') }}</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <template v-if="displayClosedVoided">
                <void :moc="moc" role class="mx-n2 my-n2"></void>
              </template>
            </v-row>
          </div>

          <div v-show="moc.changeCategory == null">
            <v-row dense>
              <v-col cols="12">
                <div v-if="displayHistory">
                  <v-row dense class="mt-3">
                    <v-col>
                      <history :moc="moc" />
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
            <v-row dense class="mt-3">
              <template v-if="activateSaveControls()">
                <v-btn v-if="isInitiator && !isSentBack" color="accent" @click="saveDraft()" class="mx-2"><span style="color: white !important;">{{
                  $t('Save_Draft')}}</span></v-btn>
                <v-btn
                  v-if="isInitiator && isSentBack"
                  color="accent"
                  @click="saveSentBack()"
                  :disabled="!saveEnabled"
                  class="mx-2"
                  ><span style="color: white !important;">{{ $t('Save') }}</span></v-btn
                >
                <v-btn v-if="isInitiator" color="success" @click="submit()" :disabled="!fullValid" class="mx-2">{{
                  $t('Submit')
                }}</v-btn>
                <template v-if="displayClosedVoided">
                  <void :moc="moc" role class="mx-n2 my-n2"></void>
                </template>
              </template>
              <v-dialog v-model="formSettings.cancelDialog" persistent max-width="290">
                <template v-slot:activator="{ props: activatorProps }">
                  <v-btn color="info" v-bind="activatorProps" class="mx-2">{{ $t('Exit_Without_Saving') }}</v-btn>
                </template>
                <v-card>
                  <v-toolbar color="warning" dense>
                    <v-toolbar-title></v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-icon class="me-3" color="black">mdi-bell-alert-outline</v-icon>
                  </v-toolbar>
                  <v-card-text class="text-h6 font-weight-bold">{{ $t('Confirm_ExitWithoutSaving') }}</v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="info" text class="card-button" @click="formSettings.cancelDialog = false">{{ $t('No') }}</v-btn>
                    <v-btn color="error" text class="card-button" @click="cancelled()">{{ $t('Yes') }}</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-row>
          </div>
        </v-container>
      </div>
    </template>

    <template v-if="formReady">
      <div class="moc-form">
        <v-container fluid>
          <support-tools v-if="displaySupportTools" :moc="moc"> </support-tools>
        </v-container>
      </div>
    </template>

    <user-group-message-dialog
      :showDialog="userGroupMessageDialogShow"
      :message="userGroupMessageDialogMessage"
      :messageOnly="userGroupMessageDialogMessageOnly"
      :user="userGroupMessageDialogUser"
      :childGroup="userGroupMessageDialogChildGroup"
      :parentGroup="userGroupMessageDialogParentGroup"
      :keepBothOnYes="userGroupMessageDialogKeepBothOnYes"
      @results="setGroupUsers($event)"
    ></user-group-message-dialog>

    <!-- temporary unauthorized expired -->
    <template v-if="temporaryActionsShow">
      <owner-temporary-actions-dialog
        :message="unauthorizedExpiredMessage"
        :tasks="tasks"
        :moc="moc"
        :important="true"
      ></owner-temporary-actions-dialog>
    </template>

    <v-dialog v-model="makePermanentDialogsShow" persistent max-width="400">
      <v-card>
        <v-toolbar color="error" height="40" window>
          <span class="ms-2 dialog-gray-text">{{ $t('Make_Permanent') }}</span>
          <v-spacer></v-spacer>
          <v-btn @click="makePermanentDialogsShow = false" icon>
            <v-icon color="black">mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-container class="dialog-gray-text">{{ $t('Make_Permanent_Submitted_Owner_Message') }}</v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="makePermanentDialogsShow = false" icon class="dialog-gray-text">{{ $t('OK') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unauthorizedExpiredGeneralPopupShow" persistent max-width="900">
      <v-card>
        <v-toolbar color="error" height="40" window>
          <span class="ms-2 dialog-gray-text">{{ $t('This_Moc_Is_Unauthorized_Expired') }}</span>
          <v-spacer></v-spacer>
          <v-btn @click="unauthorizedExpiredGeneralPopupShow = false" icon>
            <v-icon color="black">mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-container class="dialog-gray-text">{{ $t('Unauthorized_Expired_Message_General') }}</v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="unauthorizedExpiredGeneralPopupShow = false" icon class="dialog-gray-text">{{ $t('OK') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="saveAndContinueClickDialog" persistent max-width="400">
      <v-card v-if="saveAndContinueClickDialog">
        <v-toolbar color="warning" dense>
          <v-toolbar-title></v-toolbar-title>
          <v-spacer></v-spacer>
          <v-icon class="me-3" color="black">mdi-bell-alert-outline</v-icon>
        </v-toolbar>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-card-text> {{ $t('Save_And_Continue_Process_Message') }} </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="saveAndContinueClickDialog = false">{{ $t('Cancel') }}</v-btn>
                <v-btn color="primary" text @click="processAllFAT()">{{ $t('OK') }}</v-btn>
              </v-card-actions>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <toaster ref="toaster"></toaster>
    <loading-dialog ref="loadingDialog" :mocId="this.moc.mocId"></loading-dialog>
  </div>
</template>

<script>
import { FormStatus, Roles } from '@/common'
import { removeUserFromArray, alreadyRequiredUsers, arrayContainsUser, removeDuplicateUsersFromFA } from '@/common/userFunctions'
import { PeoplePickerActions } from '@/common/peoplePickerActions'
import { multiFilter } from '@/common/arrayFunctions'
import { Flag, AddedVia } from '@/common/task'
import { Flags } from '@/common/flags'
import PeoplePicker from '@/components/peoplePicker/PeoplePicker'
import PeoplePickerMs from '@/components/peoplePicker/PeoplePickerMs'
import TreeQuestions from '@/components/TreeQuestions'
import Toaster from '@/components/Toaster'
import { mapState } from 'vuex'
import MOCService from '@/services/moc.service'
import TaskService from '@/services/task.service'
import AdminService from '@/services/admin.service'
import QARelationshipService from '@/services/qarelationship.service'
//import { Promise } from 'q'
import { multiPropertySort } from '@/common/arrayFunctions'
import { ChangeTypes, TaskListTypes } from '@/common/types'
import { AdvanceType } from '@/common/systemTypes'
// moc fields
import InitiatedDateField from '@/components/mocForm/InitiatedDateField'
import CommunityField from '@/components/mocForm/CommunityField'
import IdField from '@/components/mocForm/IdField'
import TitleField from '@/components/mocForm/TitleField'
import ChangeCategoryField from '@/components/mocForm/ChangeCategoryField'
import StatusField from '@/components/mocForm/StatusField'
import TypeField from '@/components/mocForm/TypeField'
import DescriptionField from '@/components/mocForm/DescriptionField'
import JustificationField from '@/components/mocForm/JustificationField'
import StartDateField from '@/components/mocForm/StartDateField'
import ChangeTypeField from '@/components/mocForm/ChangeTypeField'
import ValidUntilDateField from '@/components/mocForm/ValidUntilDateField'
import ProcessingCommentsField from '@/components/mocForm/ProcessingCommentsField'
import FacilitatorActions from '@/components/mocForm/FacilitatorActions'
import SentBackReason from '@/components/mocForm/SentBackReason'
import Approvals from '@/components/mocForm/Approvals'
import OwnerActions from '@/components/mocForm/OwnerActions'
import InitiatorActions from '@/components/mocForm/InitiatorActions'
import ApprovedStartDateField from '@/components/mocForm/ApprovedStartDateField'
import AllTasks from '@/components/task/AllTasks'
import CoordinatorActions from '@/components/mocForm/CoordinatorActions'
import i18next from 'i18next'
import VoidReason from '@/components/mocForm/VoidReason'
import StrandedReason from '@/components/mocForm/StrandedReason'
import Comments from '@/components/mocForm/Comments'
import UserGroupMessageDialog from '@/components/mocForm/UserGroupMessageDialog'
import OwnerDecision from '@/components/mocForm/OwnerDecision'
import CloserDeniedReason from '@/components/mocForm/CloserDeniedReason'
import History from '@/components/mocForm/History'
import SupportingDocuments from '@/components/mocForm/SupportingDocuments'
import SupportingLinks from '@/components/mocForm/SupportingLinks'
import Void from '@/components/mocForm/Void'
import OwnerTemporaryActionsDialog from '@/components/mocForm/OwnerTemporaryActionsDialog'
import FlagsNotifier from '@/components/mocForm/FlagsNotifier'
import MultipassApprovers from '@/components/mocForm/MultipassApprovers'
import LoadingDialog from '@/components/mocForm/LoadingDialog'
import BeingRevisedReason from '@/components/mocForm/BeingRevisedReason'
import SupportTools from '@/components/mocForm/SupportTools'
import { EventBus } from '@/common/event-bus'

export default {
  name: 'MocForm',
  valid: true,
  components: {
    PeoplePicker,
    TreeQuestions,
    PeoplePickerMs,
    InitiatedDateField,
    CommunityField,
    IdField,
    TitleField,
    ChangeCategoryField,
    StatusField,
    TypeField,
    DescriptionField,
    JustificationField,
    StartDateField,
    ChangeTypeField,
    ValidUntilDateField,
    ProcessingCommentsField,
    FacilitatorActions,
    SentBackReason,
    Approvals,
    OwnerActions,
    InitiatorActions,
    ApprovedStartDateField,
    AllTasks,
    CoordinatorActions,
    VoidReason,
    StrandedReason,
    Comments,
    Toaster,
    UserGroupMessageDialog,
    OwnerDecision,
    CloserDeniedReason,
    History,
    SupportingDocuments,
    SupportingLinks,
    Void,
    OwnerTemporaryActionsDialog,
    FlagsNotifier,
    MultipassApprovers,
    LoadingDialog,
    BeingRevisedReason,
    SupportTools
  },
  data: () => ({
    // validation
    valid: true,
    isSaving: false,
    userGroupMessageDialogShow: false,
    userGroupMessageDialogMessage: '',
    userGroupMessageDialogMessageOnly: true,
    userGroupMessageDialogUser: {},
    userGroupMessageDialogChildGroup: [],
    userGroupMessageDialogParentGroup: [],
    userGroupMessageDialogKeepBothOnYes: false,
    makePermanentDialogsShow: false,
    initiatorChecked: false,
    formSettings: {
      mocForm: true,
      // for calendars
      startDateMenu: false,
      validUntilDateMenu: false,
      minStartDate: new Date().toISOString().split('T')[0],
      cancelDialog: false,
      closedVoidedDialog: false,
      sendBackDialog: false
    },
    changeCategories: [],
    loaded: false,
    moc: {
      changeType: {},
      status: {},
      roles: {},
      dates: {},
      communityData: {}
    },
    myRoles: [],
    previousFormStatus: {},
    questionsComplete: false,
    tasks: [],
    tasksComplete: false,
    tempAdditionalApprovers: [],
    tempAdditionalFyis: [],
    tempAllRequiredApprovers: [],
    tempAllRequiredFyis: [],
    tempAllTasks: [],
    tempAnswers: [],
    tempChangeCategory: {},
    voidReason: '',
    voidValid: false,
    viewingUsers: [],
    mocApprovals: [],
    dirtyFat: false,
    questionsDirty: false,
    supportingDocuments: [],
    supportingLinks: [],
    tasksForCurrentStatus: [],
    isOverridingDisplayNames: false,
    isCommunityConfidential: false,
    openTextEnabled: false,
    // spinny things
    archiveLoading: false,
    archiveDownloading: false,
    returnToOriginalConditionLoading: false,
    makePermanentLoading: false,
    extendLoading: false,
    // end spinny things
    unauthorizedExpiredMessage: null,
    unauthorizedExpiredDialog: false,
    flagsMessage: null,
    temporaryActionsShow: false,
    temporaryActionsInfoPanelShow: false,
    flagsMessageColor: 'bg-error',
    fatReportLoading: false,
    taskRelationshipReportLoading: false,
    unauthorizedExpiredGeneralPopupShow: false,
    saveAndContinueClickDialog: false,
    showOriginalDecision: false,
    formLoaded: false,
    deletingUser: false,
    currentSavedStatusValue:null
  }),
  props: {
    // id: {
    //   type: [String, null],
    //   required: true
    // }
    id: String
  },
  methods: {
    activateSaveControls: function () {
      if (this.saveAndContinueDisable) {
        return true
      }
      return false
    },
    saveAndContinueDisable: function () {
      if (this.isDirtyFat && this.isQuestionsValid()) {
        return false
      }
      if (this.isQuestionsDirty && this.isQuestionsValid()) {
      }
      return true
    },
    getPeoplePickerInjectedProperties() {
      return {
        confidentialOptions: {
          confidentialCommunityAbbreviation: this.isCommunityConfidential ? this.$route.params.shortName : undefined
        }
      }
    },
    async saveAndContinueClick() {
      console.log("MOC ID:", this.moc.mocId);
      // first lets check to make sure the current user is working on an MOC that has not changed status while they had it open
      const currentSavedStatusValue = await this.getSavedMocStatusValue(this.moc.mocId);
       console.log("Fetched status value:", currentSavedStatusValue);
       console.log("Local status value:", this.moc.status.value);

      if (currentSavedStatusValue != null && this.moc.status.value !== currentSavedStatusValue) {
        this.$refs.loadingDialog.error({ message: this.$t('Someone_Else_Already_Change_This') })
      }
    else if (
        (this.tasks.length > 0 && this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value) ||
        this.moc.status.value === FormStatus.properties[FormStatus.BEINGREVISED].value
      ) {
        this.saveAndContinueClickDialog = true
      } else {
        await this.processAllFAT()
      }
    },
    loadSupportingLinks(data) {
      this.supportingLinks = data
    },
    loadSupportingDocuments(data) {
      this.supportingDocuments = data
    },
    getTasksForCurrentStatus(tasks) {
      this.tasksForCurrentStatus = tasks
    },
    setMocApprovals(approvals) {
      this.mocApprovals = approvals
      if (this.moc.multiPassCurrentPass === 0) {
        this.moc.roles.additionalApprovers = (this.moc.roles.additionalApprovers || []).map((u) => ({
          ...u,
          readonly: this.setReadonlyAdditionApprover(u)
        }))
      }
    },
    toaster() {
      return this.$refs.toaster
    },
    areQuestionsCompleted: function () {
      if (this.moc.answeredQuestions !== null) {
        let inQuestions = [...this.moc.answeredQuestions]
        if (inQuestions) {
          let unansweredQuestions = inQuestions.filter((q) => {
            return (this.isQuestionRequired(q.id) && q.answers.length === 0) || q.answers === null
          })

          if (unansweredQuestions.length === 0) {
            this.questionsComplete = true
          } else {
            this.questionsComplete = false
          }
        }
      } else {
        this.questionsComplete = true
      }
    },
    isQuestionRequired: function (questionId) {
      if (this.moc.communityData.questions) {
        this.moc.communityData.questions.filter((q) => {
          if (q.id === questionId && q.required === true) {
            return true
          }
        })
      }
      return false
    },
    areTasksCompleted: function () {
      let filteredTasks = []
      if (this.tasks) {
        switch (this.moc.status.value) {
          case FormStatus.properties[FormStatus.NEW].value:
          case FormStatus.properties[FormStatus.DRAFT].value:
          case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value: {
            filteredTasks = multiFilter(this.tasks, {
              taskListType: { key: `${TaskListTypes.SUBMIT.toString()}` }
            })
            break
          }
          case FormStatus.properties[FormStatus.SUBMITTED].value: {
            filteredTasks = multiFilter(this.tasks, {
              taskListType: { key: `${TaskListTypes.SENDFORDECISION.toString()}` }
            })
            break
          }
          case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value: {
            filteredTasks = multiFilter(this.tasks, {
              taskListType: { key: `${TaskListTypes.ENABLEOWNERDECISIONTOOCCUR.toString()}` }
            })
            break
          }
          case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value: {
            filteredTasks = multiFilter(this.tasks, {
              taskListType: { key: `${TaskListTypes.PROCEEDWITHIMPLEMENTATION.toString()}` }
            })
            break
          }
          case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value: {
            filteredTasks = multiFilter(this.tasks, {
              taskListType: { key: `${TaskListTypes.CONFIRMCHANGEISINPLACE.toString()}` }
            })
            break
          }
          case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value: {
            filteredTasks = multiFilter(this.tasks, {
              taskListType: { key: `${TaskListTypes.CONFIRMCHANGEISFULLYIMPLEMENTED.toString()}` }
            })
            break
          }
          default:
        }

        if ((filteredTasks | []).length === 0) {
          this.tasksComplete = true
          return
        }

        let completedSubmitTasks = filteredTasks.filter((t) => {
          return t.completed === true
        })

        if (filteredTasks.length === completedSubmitTasks.length) {
          this.tasksComplete = true
        } else {
          this.tasksComplete = false
        }
      }
    },
    createNewMoc() {
      return new Promise((resolve, reject) => {
        MOCService.getNewMoc(this.$route.params.shortName)
          .then((data) => {
            this.moc = data
            // console.log(`getNewMoc ${JSON.stringify(data)}`)
          })
          .then(() => {
            this.myRoles.push('initiator')
            this.setChangeType()
            this.setChangeCategories()
            this.$store.dispatch('currentMoc/setId', this.moc.mocId)
            this.$store.dispatch('currentMoc/setStatus', this.moc.status)
            this.$store.dispatch('currentMoc/setFormColor', 'backgroundNormal')

            resolve()
          })
          .catch((e) => {
            // TODO: determine if we need to do anything here
            this.$refs.loadingDialog.error({ metadata: 'createNewMoc', error: e, message: this.$t('Error_CreateNewMoc') })
            reject(e)
          })
          .finally(() => {
            this.setLoaded(true)
          })
      })
    },
async getSavedMocStatusValue(mocId) {
    try {
      const data = await MOCService.getMocStatus(mocId);
      //this.currentMocStatusValue = data.value;
      return data.value;
    } catch (error) {
      console.error("Failed to get MOC status:", error);
      return null;
    }
  },    
  handleChangeCategoryChanged: function () {
      console.log(`handleChangeCategoryChanged enter`)
      this.dirtyFat = true
      
      if (this.moc) {
        this.dirtyFat = true
        this.isQuestionsValid()
        // this is what drives the form so if it is changed, we are wiping
        // approvers, reviews and tasks.
        // this.moc.mocType = ''
        this.moc.mocType = null
        this.tempAllRequiredApprovers = []
        this.tempAllRequiredFyis = []
        this.tempAdditionalApprovers = []
        this.tempAdditionalFyis = []
        this.tempAllTasks = []
        this.tasks = []

        let ccName = this.moc.changeCategory
        let index = this.$_.findIndex(this.moc.communityData.changeCategories, function (o) {
          return o.name === ccName
        })

        // add facilitator and backup
        if (this.moc.communityData.changeCategories[index].facilitator) {
          this.moc.roles.facilitator = this.moc.communityData.changeCategories[index].facilitator
        }
        if (this.moc.communityData.changeCategories[index].backupFacilitator) {
          this.moc.roles.facilitatorBackup = this.moc.communityData.changeCategories[index].backupFacilitator
        }
      }
    },
    handleChangeTypeChanged: function (val) {
      this.dirtyFat = true
      if (val.type.key === ChangeTypes.PERMANENT.toString()) {
        this.moc.dates.validUntil = null
      }
      this.$refs.mocForm.validate()
    },
    handleDeleteAdditionalApprovers: function (removedUser, people, done) {
      this.deletingUser = true
      if (
        this.moc.status.value !== this.newStatus &&
        this.moc.status.value !== this.draftStatus &&
        this.moc.status.value !== this.submittedStatus &&
        this.moc.status.value !== this.sentBackStatus &&
        this.moc.status.value !== this.beingRevisedStatus
      ) {
        const currentUser = this.$store.state.currentUser.user
        this.$refs.approvals.getApprovals(this.moc.mocId).then(async (data) => {
          this.$refs.approvals.processApprovals(data, currentUser)

          let ruser = null
          // the else part of this should never be hit as multipass is handled in it own component. Something worth looking in to later
          if (this.moc.multiPassCurrentPass === 0) {
            ruser = this.moc.roles.additionalApprovers.filter((ru) => {
              return ru.id === removedUser.id && ru.decisionPass === this.moc.multiPassCurrentPass
            })
          } else {
            ruser = this.moc.approvalProcessType.multiPass.approvalPasses[
              this.moc.multiPassCurrentPass - 1
            ].additionalApprovers.filter((ru) => {
              return ru.id === removedUser.id && ru.decisionPass === this.moc.multiPassCurrentPass
            })
          }

          if ((ruser || {}).length > 0) {
            if (!ruser[0].readonly) {
              let tempAdditionalApprovers = removeUserFromArray(ruser[0], this.moc.roles.additionalApprovers)
              this.moc.roles.additionalApprovers = tempAdditionalApprovers
              done()
            }
          }
        })
      } else {
        // we do not have the approvals component on the page so therefore we can just delete the user
        let ruser = this.moc.roles.additionalApprovers.filter((ru) => {
          return ru.id === removedUser.id && (ru.decisionPass === this.moc.multiPassCurrentPass || ru.decisionPass === undefined)
        })

        if ((ruser || {}).length > 0) {
          if (!ruser[0].readonly) {
            let tempAdditionalApprovers = removeUserFromArray(ruser[0], this.moc.roles.additionalApprovers)
            this.moc.roles.additionalApprovers = tempAdditionalApprovers
            done()
          }
        }
      }
      this.deletingUser = false
    },
    handleInitiatorsChange: function () {
      console.log(`moc.vue handleInitiatorsChange`)
      if (
        (this.moc.roles.initiator && this.moc.roles.initiator.id) ===
        (this.moc.roles.coinitiator && this.moc.roles.coinitiator.id)
      ) {
        this.$refs.toaster.pop('info', `${this.$t('Info_AlreadyInitiator')}`)
        this.$refs.coinitiator.removeSelectedUser()
      }
    },
    handleMocTypeChange: function () {
      this.dirtyFat = true
    },
    handleQuestionsDirty: function (val) {
      this.questionsDirty = val
    },
    handleTaskCompleted: function () {
      this.areTasksCompleted()
    },
    handleUpdateFyis: function (userAction) {
      if (userAction && userAction.Users.length > 0) {
        let fyiUsers = userAction.Users
        this.moc.roles.fyis = fyiUsers
      }
    },
    handleUpdateApprovers: function (userAction) {
      if (userAction && userAction.Users.length > 0) {
        this.moc.roles.approvers = userAction.Users
      }
    },
    handleUpdateAdditionalFyis: function (usersAction) {
      if (this.formLoaded) {
        let user = []
        // only check the currently added user
        if (usersAction.Users && usersAction.Users.length > 0) {
          user.push(usersAction.Users[usersAction.Users.length - 1])
        }

        let fyiUsers = usersAction.Users
        if (usersAction.Action === PeoplePickerActions.properties[PeoplePickerActions.ADD].value) {
          let arePresetApproversUsers = alreadyRequiredUsers(this.moc.roles.approvers, user)
          let arePresetFyisUsers = alreadyRequiredUsers(this.moc.roles.fyis, user)
          let areAdditionalApproverUsers = alreadyRequiredUsers(this.moc.roles.additionalApprovers, user)

          // remove owner from fyis
          if (
            this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value &&
            (this.myRoles.includes(Roles.properties[Roles.FACILITATOR].value) ||
              this.myRoles.includes(Roles.properties[Roles.FACILITATORBACKUP].value))
          ) {
            if (arrayContainsUser(fyiUsers, this.moc.roles.owner)) {
              this.userGroupMessageDialogShow = true
              this.userGroupMessageDialogMessage = this.$t('Info_AlreadyOwner', { person: this.moc.roles.owner.displayName })
              this.tempAdditionalFyis = removeUserFromArray(this.moc.roles.owner, fyiUsers)
              this.moc.roles.additionalFyis = this.tempAdditionalFyis
            }
          }
          if (arePresetApproversUsers.length > 0) {
            arePresetApproversUsers.forEach((u) => {
              this.userGroupMessageDialogShow = true
              this.userGroupMessageDialogMessage = this.$t('Info_AlreadyHasRole', { person: u.displayName })
            })
          }
          if (arePresetFyisUsers.length > 0) {
            arePresetFyisUsers.forEach((u) => {
              this.userGroupMessageDialogShow = true
              this.userGroupMessageDialogMessage = this.$t('Info_AlreadyHasRole', { person: u.displayName })
            })
          }
          if (areAdditionalApproverUsers.length > 0) {
            areAdditionalApproverUsers.forEach((u) => {
              this.userGroupMessageDialogShow = true
              this.userGroupMessageDialogMessage = this.$t('Info_AlreadyHasRole', { person: u.displayName })
            })
          }
        }
        this.moc.roles.additionalFyis = fyiUsers
      }
    },
    handleUpdateAdditionalApprovers: function (usersAction) {
      if (this.formLoaded && !this.deletingUser) {
        let user = []
        // only check the currently added user
        if (usersAction.Users && usersAction.Users.length > 0) {
          user.push(usersAction.Users[usersAction.Users.length - 1])
        }

        let approverUsers = usersAction.Users
        if (usersAction.Action === PeoplePickerActions.properties[PeoplePickerActions.ADD].value) {
          let arePresetApproversUsers = alreadyRequiredUsers(this.moc.roles.approvers, user)
          let arePresetFyisUsers = alreadyRequiredUsers(this.moc.roles.fyis, user)
          let areAdditionalFyisUsers = alreadyRequiredUsers(this.moc.roles.additionalFyis, user)

          // remove owner from approvers
          if (
            this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value &&
            (this.myRoles.includes(Roles.properties[Roles.FACILITATOR].value) ||
              this.myRoles.includes(Roles.properties[Roles.FACILITATORBACKUP].value))
          ) {
            if (arrayContainsUser(approverUsers, this.moc.roles.owner)) {
              this.userGroupMessageDialogShow = true
              this.userGroupMessageDialogMessage = this.$t('Info_AlreadyOwner', { person: this.moc.roles.owner.displayName })
              this.tempAdditionalApprovers = removeUserFromArray(this.moc.roles.owner, approverUsers)
              this.moc.roles.additionalApprovers = this.tempAdditionalApprovers
            }
          }

          if (arePresetApproversUsers.length > 0) {
            arePresetApproversUsers.forEach((u) => {
              this.userGroupMessageDialogShow = true
              this.userGroupMessageDialogMessage = this.$t('Info_AlreadyHasRole', { person: u.displayName })
            })
          }
          arePresetFyisUsers.forEach((u) => {
            this.userGroupMessageDialogShow = true
            this.userGroupMessageDialogMessage = this.$t('Info_AlreadyHasRole', { person: u.displayName })
          })

          areAdditionalFyisUsers.forEach((u) => {
            this.userGroupMessageDialogShow = true
            this.userGroupMessageDialogMessage = this.$t('Info_AlreadyHasRole', { person: u.displayName })
          })
        }

        // when adding new approvers the current pass is important.  Without the current pass, you cannot delete if added by mistake.
        approverUsers.forEach((u) => {
          'decisionPass' in u || (u.decisionPass = this.moc.multiPassCurrentPass)
        })

        this.moc.roles.additionalApprovers = approverUsers
      }
    },
    isQuestionsValid: function () {
      if (this.$refs.mocForm) {
        return this.$refs.mocForm.validate()
      }
    },
    loadChangeCategoryTasks: function () {
      if (this.moc.communityData.tasks) {
        // Add change Category Tasks
        // get the change category task ids associated with the change category
        let cc = this.moc.communityData.changeCategories.filter((c) => {
          return c.name === this.moc.changeCategory
        })
        
        if (cc[0] && cc[0].tasks !== null) {
          // get the tasks from the master task list where the ids match the change type task ids
          let tasks = this.moc.communityData.tasks.filter((mt) => {
            return (
              cc[0].tasks.indexOf(mt.id) !== -1 &&
              mt.active === true &&
              (mt.relatedChangeType === ChangeTypes.properties[this.moc.changeType.type.key].key ||
                mt.relatedChangeType === ChangeTypes.properties[ChangeTypes.BOTH].key)
            )
          })

          // adding flags for dirty and how added (flag, addedVia)
          tasks.forEach((t) => {
            t.flag = Flag.DIRTY
            t.addedVia = AddedVia.CHANGECATEGORY
            t.mocId = this.moc.mocId
            t.taskId = t.id
            t.completedDate = null
          })

          // dedupe tasks
          let concatAndDeDuplicateTasks = (p, ...arrs) => [
            ...new Set(
              [].concat(...arrs).filter((obj, pos, arr) => {
                return arr.map((mapObj) => mapObj[p]).indexOf(obj[p]) === pos
              })
            )
          ]

          this.tasks = concatAndDeDuplicateTasks('id', this.tasks, tasks)
        }
      }
    },
    loadChangeTypeTasks: function () {
      if (this.moc.communityData.tasks) {
        switch (this.moc.changeType.type.key) {
          case ChangeTypes.PERMANENT.toString(): {
            // first remove any temporary tasks
            if (this.tasks) {
              let filteredTasks = this.tasks.filter(
                (t) =>
                  t.relatedChangeType === ChangeTypes.PERMANENT.toString() || t.relatedChangeType === ChangeTypes.BOTH.toString()
              )
              this.tasks = filteredTasks
            }
            // get the change type task ids associated with the change type
            let ct = this.moc.communityData.changeTypes.filter((ct) => {
              return ct.type.key === ChangeTypes.properties[ChangeTypes.PERMANENT].key
            })
            if (ct[0] && ct[0].tasks !== null) {
              // get the tasks from the master task list where the ids match the change type task ids
              let tasks = this.moc.communityData.tasks.filter((mt) => {
                return (
                  ct[0].tasks.indexOf(mt.id) !== -1 &&
                  mt.active === true &&
                  (mt.relatedChangeType === ChangeTypes.properties[this.moc.changeType.type.key].key ||
                    mt.relatedChangeType === ChangeTypes.properties[ChangeTypes.BOTH].key)
                )
              })

              // adding flags for dirty and how added (flag, addedVia)
              tasks.forEach((t) => {
                t.flag = Flag.DIRTY
                t.addedVia = AddedVia.CHANGETYPE
                t.mocId = this.moc.mocId
                t.taskId = t.id
                t.completedDate = null
              })

              // dedupe tasks
              let concatAndDeDuplicateTasks = (p, ...arrs) => [
                ...new Set(
                  [].concat(...arrs).filter((obj, pos, arr) => {
                    return arr.map((mapObj) => mapObj[p]).indexOf(obj[p]) === pos
                  })
                )
              ]

              this.tasks = concatAndDeDuplicateTasks('id', this.tasks, tasks)
            }
            break
          }
          case ChangeTypes.TEMPORARY.toString():
            {
              // first remove any permanent tasks
              if (this.tasks) {
                let filteredTasks = this.tasks.filter(
                  (t) =>
                    t.relatedChangeType === ChangeTypes.TEMPORARY.toString() ||
                    t.relatedChangeType === ChangeTypes.BOTH.toString()
                )
                this.tasks = filteredTasks
              }

              // get the change type task ids associated with the change type
              let ct = this.moc.communityData.changeTypes.filter((ct) => {
                return ct.type.key === ChangeTypes.properties[ChangeTypes.TEMPORARY].key
              })

              if (ct[0] && ct[0].tasks !== null) {
                // get the tasks from the master task list where the ids match the change type task ids
                let tasks = this.moc.communityData.tasks.filter((mt) => {
                  return (
                    ct[0].tasks.indexOf(mt.id) !== -1 &&
                    mt.active === true &&
                    (mt.relatedChangeType === ChangeTypes.properties[this.moc.changeType.type.key].key ||
                      mt.relatedChangeType === ChangeTypes.properties[ChangeTypes.BOTH].key)
                  )
                })

                // adding flags for dirty and how added (flag, addedVia)
                tasks.forEach((t) => {
                  t.flag = Flag.DIRTY
                  t.addedVia = AddedVia.CHANGETYPE
                  t.mocId = this.moc.mocId
                  t.taskId = t.id
                  t.completedDate = null
                })

                // dedupe tasks
                let concatAndDeDuplicateTasks = (p, ...arrs) => [
                  ...new Set(
                    [].concat(...arrs).filter((obj, pos, arr) => {
                      return arr.map((mapObj) => mapObj[p]).indexOf(obj[p]) === pos
                    })
                  )
                ]

                this.tasks = concatAndDeDuplicateTasks('id', this.tasks, tasks)
              }
            }
            break
          default:
            break
        }
      }
    },
    loadMocTypeTasks: function () {
      // If the community has tasks
      if (this.moc.communityData.tasks) {
        // Add change Category-mocType Tasks
        // get the change category-mocType task ids associated with the change category-mocType
        let cc = this.moc.communityData.changeCategories.filter((c) => {
          return c.name === this.moc.changeCategory
        })

        if (cc[0] && cc[0].mocTypes !== null) {
          let mocType = cc[0].mocTypes.filter((c) => {
            return c.value === this.moc.mocType
          })
          
          if (mocType[0] && mocType[0].tasks !== null) {
            // get the tasks from the master task list where the ids match the change type task ids
            let tasks = this.moc.communityData.tasks.filter((mt) => {
              return (
                mocType[0].tasks.indexOf(mt.id) !== -1 &&
                mt.active === true &&
                (mt.relatedChangeType === ChangeTypes.properties[this.moc.changeType.type.key].key ||
                  mt.relatedChangeType === ChangeTypes.properties[ChangeTypes.BOTH].key)
              )
            })

            // adding flags for dirty and how added (flag, addedVia)
            tasks.forEach((t) => {
              t.flag = Flag.DIRTY
              t.addedVia = AddedVia.MOCTYPE
              t.mocId = this.moc.mocId
              t.taskId = t.id
              t.completedDate = null
            })

            // dedupe tasks
            let concatAndDeDuplicateTasks = (p, ...arrs) => [
              ...new Set(
                [].concat(...arrs).filter((obj, pos, arr) => {
                  return arr.map((mapObj) => mapObj[p]).indexOf(obj[p]) === pos
                })
              )
            ]

            this.tasks = concatAndDeDuplicateTasks('id', this.tasks, tasks)
          }
        }
      }
    },
    loadTasks: function () {
      return TaskService.getAllTasksForMoc(this.moc.mocId).then((data) => {
        this.tasks = data
      })
    },
    setOwner: function (value) {
      if (this.moc) {
        this.moc.roles.owner = value
      }
    },
    setCoordinator: function (value) {
      if (this.moc) {
        this.moc.roles.coordinator = value
      }
    },
    setFacilitator: function (value) {
      if (this.moc) {
        this.moc.roles.facilitator = value
      }
    },
    setFacilitatorBackup: function (value) {
      if (this.moc) {
        this.moc.roles.facilitatorBackup = value
      }
    },
    setLoaded: function (val) {
      this.loaded = val
    },
    approvalProcessTypeChanged: function (value) {
      if (this.moc) {
        this.moc.approvalProcessType = value
        const approvalProcessTypeOwner = (this.moc.approvalProcessType || {}).owner
        this.moc.roles.owner = approvalProcessTypeOwner && approvalProcessTypeOwner.displayName ? approvalProcessTypeOwner : null
        if (this.moc.approvalProcessType) {
          // Add the approvers and fyis from approval process type
          if (this.moc.approvalProcessType) {
            // remove
            this.removeRequiredApprovers('apt')
            this.removeRequiredFyis('apt')
            // add
            this.addRequiredApprovers(this.moc.approvalProcessType.approvers, 'apt')
            this.addRequiredFyis(this.moc.approvalProcessType.fyis, 'apt')
          }
        }
      }
    },
    handleQuestionAnswered: function (answeredQuestion) {
      console.log(`moc handleQuestionAnswered`)
      if (answeredQuestion !== undefined) {
        this.moc.answeredQuestions = answeredQuestion
        this.areQuestionsCompleted()
      }
      this.$refs.mocForm.validate()
    },
    getChangeCategories: function () {
      if (this.moc.communityData) {
        const sorted = this.$_.sortBy(
          this.moc.communityData.changeCategories.filter((cg) => cg.enabled),
          ['name']
        )
        return sorted
      } else {
        return null
      }
    },
    processFAT: async function () {
      // remove the FAT from F
      this.tempAllRequiredFyis = []

      // remove the FAT from A
      this.tempAllRequiredApprovers = []

      // add some FAT
      const cc = this.moc.changeCategory
      const index = this.$_.findIndex(this.moc.communityData.changeCategories, function (o) {
        return o.name === cc
      })

      // Add the approvers and Fyis from moctype
      if (this.moc.mocType) {
        const moctype = this.moc.mocType
        const mocTypeIndex = this.$_.findIndex(this.moc.communityData.changeCategories[index].mocTypes, function (o) {
          return o.value === moctype
        })
        const mocType = this.moc.communityData.changeCategories[index].mocTypes[mocTypeIndex]

        // add some FAT
        this.addRequiredApprovers(mocType.approvers)
        this.addRequiredFyis(mocType.fyis)
      }

      // Add the approvers and fyis from approval process type
      if (this.moc.approvalProcessType) {
        this.addRequiredApprovers(this.moc.approvalProcessType.approvers)
        this.addRequiredFyis(this.moc.approvalProcessType.fyis)
      }

      // this is necessary as we are wiping all the FA above
      if (this.moc.communityData.questions.length > 0) {
        await this.processQA()
      } else {
        await this.saveAndCleanUpTasks()
      }
    },
    processQAFat: async function (fat) {
      let tasks = []

      fat.forEach((fa) => {
        this.addRequiredFyis(fa.fyis)
        this.addRequiredApprovers(fa.approvers)
        tasks = [...tasks, ...this.getTasksFromId(fa.tasks)]
      })

      // we have to store tasks in case a task was satisfied and the task is re-introduced,
      // note: that was the plan, but the plan now is to keep the completed task needed or not
      let holdSatisfiedTasks = this.tasks.filter((t) => {
        return t.addedVia === AddedVia.QARELATIONSHIP && (t.status || {}).value === 'Completed'
      })

      let holdManualAddedTasks = this.tasks.filter((t) => {
        return t.addedVia === AddedVia.MANUAL
      })

      // remove all QARelationship tasks
      this.tasks = this.tasks.filter((t) => {
        return t.addedVia !== AddedVia.QARELATIONSHIP
      })
      this.$forceUpdate()

      // adding flags for dirty and how added (flag, addedVia)
      tasks.forEach((t) => {
        t.flag = Flag.DIRTY
        t.addedVia = AddedVia.QARELATIONSHIP
        t.mocId = this.moc.mocId
        t.taskId = t.id
        t.completedDate = null
      })

      // dedupe tasks
      let concatAndDeDuplicateTasks = (p, ...arrs) => [
        ...new Set(
          [].concat(...arrs).filter((obj, pos, arr) => {
            return arr.map((mapObj) => mapObj[p]).indexOf(obj[p]) === pos
          })
        )
      ]

      // add all generated tasks
      this.tasks = concatAndDeDuplicateTasks('taskId', tasks, this.tasks)

      // remove satisfied tasks that no longer apply to this moc
      holdSatisfiedTasks = holdSatisfiedTasks.filter((t) => tasks.findIndex((x) => x.taskId === t.taskId) > -1)

      //add back the completed tasks
      this.tasks = concatAndDeDuplicateTasks('taskId', holdSatisfiedTasks, this.tasks)

      //add back the manual tasks
      this.tasks = concatAndDeDuplicateTasks('taskId', holdManualAddedTasks, this.tasks)
    },
    getTasksFromId: function (taskIds) {
      let results = this.moc.communityData.tasks.filter((t) => {
        return taskIds.some((i) => {
          return i === t.id
        })
      })

      return results
    },
    cancelled: function () {
      this.formSettings.cancelDialog = false
      this.$router.push({
        path: `/${this.moc.communityData.communityAbbreviation}/CommunityDashboard`
      })
    },
    fetchMoc(communityAbbrev, id) {
      this.setLoaded(false)
      // we have a live one here!
      return MOCService.getMocById(communityAbbrev, id).then((data) => {
        this.moc = data.item1
        const roles = data.item2
        const impersonatedRoles = this.getImpersonatedRoles(data.item1.mocId)
        this.myRoles = Array.from(new Set(roles.concat(impersonatedRoles)))
        this.previousFormStatus = data.item3

        this.tempAdditionalApprovers = this.moc.roles.additionalApprovers
        this.tempAdditionalFyis = this.moc.roles.additionalFyis
        this.setLoaded(true)
      })
    },
    getImpersonatedRoles(mocId) {
      const result = sessionStorage.getItem(mocId)
      const roles = result ? JSON.parse(result) : []
      return roles
    },
    displayDateFormat: (date) => {
      return date.substr(0, 10)
    },
    getQuestionFAT: function (question, fat) {
      if (question.values) {
        question.values.forEach((v) => {
          fat.fyis.push(...v.fyis)
          fat.approvers.push(...v.approvers)
          fat.tasks.push(...v.tasks)

          if (v.questions) {
            v.questions.forEach((currentQuestion) => {
              this.findFAT(currentQuestion, fat)
            })
          }

          return fat
        })
      }
    },
    findFAT: function (valueIds, question, fat) {
      if (question.values) {
        question.values.forEach((v) => {
          valueIds.forEach((vid) => {
            if (v.id === vid) {
              fat.fyis.push(...v.fyis)
              fat.approvers.push(...v.approvers)
              fat.tasks.push(...v.tasks)

              if (v.questions) {
                v.questions.forEach((currentQuestion) => {
                  this.findFAT(valueIds, currentQuestion, fat)
                })
              }

              return fat
            }
          })
        })
      }
    },
    compare: function (otherArray) {
      return function (current) {
        return (
          otherArray.filter(function (other) {
            return other.id === current.id && JSON.stringify(other.answers) === JSON.stringify(current.answers)
          }).length === 0
        )
      }
    },
    addRequiredApprovers: function (approvers, addedVia) {
      if (approvers) {
        // first add approvers to the pool this is because a user can be an approver because of multiple answers

        if (addedVia) {
          approvers.forEach((a) => {
            a.addedVia = addedVia
          })
        }

        this.tempAllRequiredApprovers = (this.tempAllRequiredApprovers || []).concat(approvers)
        this.tempAllRequiredApprovers = removeDuplicateUsersFromFA(this.tempAllRequiredApprovers)
        this.moc.roles.approvers = this.tempAllRequiredApprovers
      }
    },
    addRequiredFyis: function (fyis, addedVia) {
      if (fyis) {
        if (addedVia) {
          fyis.forEach((f) => {
            f.addedVia = addedVia
          })
        }
        this.tempAllRequiredFyis = (this.tempAllRequiredFyis || []).concat(fyis)
        this.tempAllRequiredFyis = removeDuplicateUsersFromFA(this.tempAllRequiredFyis)
        this.moc.roles.fyis = this.tempAllRequiredFyis
      }
    },
    removeRequiredApprovers: function (addedVia) {
      this.tempAllRequiredApprovers = [...this.moc.roles.approvers]

      this.tempAllRequiredApprovers = this.tempAllRequiredApprovers.filter((a) => {
        return a.addedVia !== addedVia
      })
      this.moc.roles.approvers = this.tempAllRequiredApprovers
    },
    removeRequiredFyis: function (addedVia) {
      this.tempAllRequiredFyis = [...this.moc.roles.fyis]
      this.tempAllRequiredFyis = this.tempAllRequiredFyis.filter((f) => {
        return f.addedVia !== addedVia
      })
      this.moc.roles.fyis = this.tempAllRequiredFyis
    },
    setReadonlyAdditionApprover(user) {
      if (!this.readonlyAdditionalApprovers && this.mocApprovals && this.mocApprovals.length > 0) {
        const isUserWithDecision = this.mocApprovals.filter((a) => a.decision.key).some((u) => u.user.id === user.id)
        return isUserWithDecision
      }
      return false
    },
    addAdditionalApprovers: function (approvers) {
      if (approvers) {
        // first add approvers to the pool this is because a user can be an approver because of multiple answers
        if (this.tempAdditionalApprovers == null) {
          this.tempAdditionalApprovers = []
        }
        let approverUsers = approvers
        let arePresetUsers = alreadyRequiredUsers(this.moc.roles.approvers, approvers)

        this.tempAdditionalApprovers = this.tempAdditionalApprovers.concat(approverUsers)
        arePresetUsers.forEach((u) => {
          this.tempAdditionalApprovers = removeUserFromArray(u, this.tempAdditionalApprovers)
        })
      }
    },
    addAdditionalFyis: function (fyis) {
      if (fyis) {
        if (this.tempAdditionalFyis == null) {
          this.tempAdditionalFyis = []
        }
        this.tempAdditionalFyis = [...new Set([...this.tempAdditionalFyis, ...fyis])]
        this.moc.roles.additionalFyis = this.tempAdditionalFyis
      }
    },
    submit: function () {
      if (this.valid) {
        if (!this.isSaving) {
          this.isSaving = true
          let successMessage = this.$t('Submitted_Successfully')
          let redirect = true
          const currentStatus = this.moc.status
          // blank out the sent back reason on re-submitRevisionDescription
          if (this.moc.sentBackReason) {
            this.moc.sentBackReason = ''
          }
          const data = { ...this.moc }
          this.$refs.loadingDialog.show()
          MOCService.save(data, AdvanceType.ADVANCE)
            .then((result) => {
              if (!result.success) {
                this.$refs.loadingDialog.hide()
                if (result.message === 'Tasks not all approved cannot move on.') {
                  throw new Error('TaskError1')
                }
                if (result.message === 'Someone_Else_Already_Change_This') {
                  throw new Error(result.message)
                }
                this.dirtyFat = true
                throw new Error('TaskError2')
              }
            })
            .then(() => {
              this.$refs.loadingDialog.hide({ redirect, message: successMessage })
            })
            .catch((e) => {
              switch (e.message) {
                case 'TaskError1':
                  this.$refs.loadingDialog.warning({
                    metadata: this.$t('Task_Issue_Not_All_Complete_Message'),
                    warning: { metadata: 'xxx' },
                    message: 'Task Error'
                  })
                  break
                case 'TaskError2':
                  this.$refs.loadingDialog.warning({
                    metadata: this.$t('Task_Issue_Message'),
                    warning: { metadata: 'xxx' },
                    message: 'Task Error'
                  })
                  break
                case 'Someone_Else_Already_Change_This':
                  this.$refs.loadingDialog.error({ metadata: 'submit', error: e, message: this.$t(e.message) })
                  if (e.response && e.response.status === 400) {
                    this.moc.status = currentStatus
                  }
                  break
                default:
                  this.$refs.loadingDialog.error({ metadata: 'submit', error: e, message: this.$t('Error_SaveSubmit') })
                  if (e.response && e.response.status === 400) {
                    this.moc.status = currentStatus
                  }
                  break
              }
            })
            .finally(() => {
              this.isSaving = false
            })
        }
      }
    },
    closedVoided: function () {
      if (!this.isSaving) {
        this.isSaving = true
        const currentStatus = this.moc.status
        this.moc.closedVoidedReason = { reason: this.voidReason }
        const data = this.moc
        this.$refs.loadingDialog.show()
        MOCService.save(data, AdvanceType.CLOSEVOID)
          .then((result) => {
            if (!result.success) {
              throw new Error(result.message)
            }
            this.$refs.loadingDialog.hide({ redirect: true, message: this.$t('Success_ClosedVoidedSaved') })
          })
          .catch((e) => {
            this.isSaving = false
            if (e.response && e.response.status === 400) {
              this.moc.status = currentStatus
            }
            this.$refs.loadingDialog.error({ metadata: 'closedVoided', error: e, message: this.$t('Error_CloseVoid') })
          })
          .finally(() => {
            this.isSaving = false
          })
      }
    },
    cancel: () => {},
    deleteAllTasksForMoc: async function () {
      await TaskService.deleteAllTasksForMoc(this.moc.mocId)
    },
    loadForm: async function () {
      this.setChangeCategories()
      this.populateFormFAT()
      await this.loadTasks().then(this.areTasksCompleted)

      this.$store.dispatch('currentMoc/setFormColor', 'backgroundNormal')

      // this is a special case due to the fact of poor requirements
      // this is when you have only 1 moctype and 1 changeCat and 1 APT
      // so this case will only ever get hit on a dev box...
      if (this.moc.communityData.questions.length === 0) {
        await this.processFAT()
      }

      // display flags

      // this.moc.flags = [
      //   "returntooriginalcondition",
      //   "unauthorized"
      // ];

      if ((this.moc.flags || []).length > 0) {
        let message = ''

        if (this.moc.flags.includes(Flags.properties[Flags.MULTIPASS].key)) {
          if (this.moc.multiPassCurrentPass > 0) {
            this.flagsMessage = this.moc.approvalProcessType.multiPass.approvalPasses[this.moc.multiPassCurrentPass - 1].label
            this.flagsMessageColor = 'bg-indigo-darken-4'
          }
        } else {
          this.moc.flags.forEach((f) => {
            message = message.concat(Flags.properties[f].value).concat(' - ')
          })
          this.flagsMessage = message.slice(0, -3)
        }
      }

      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
        (this.moc.status.value ===
          FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value &&
          !this.moc.flags.includes(Flags.properties[Flags.RETURNTOORIGINALCONDITION].key))
      ) {
        if (this.moc.changeType.type.key === ChangeTypes.properties[ChangeTypes.TEMPORARY].key) {
          if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
            this.temporaryActionsInfoPanelShow = true
          }
        }
      }

      if (
        this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
        this.moc.status.value ===
          FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value
      ) {
        if (this.moc.changeType.type.key === ChangeTypes.properties[ChangeTypes.TEMPORARY].key) {
          if (this.moc.flags && this.moc.flags.includes(Flags.properties[Flags.UNAUTHORIZED].key)) {
            if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
              if (!this.moc.flags.includes(Flags.properties[Flags.OWNEREDITABLEMAKEPERMANENT].key)) {
                this.unauthorizedExpiredMessage = this.$t('Unauthorized_Expired_Message_Owner')
                if (
                  !this.moc.flags.includes(Flags.properties[Flags.RETURNTOORIGINALCONDITION].key) &&
                  !this.moc.flags.includes(Flags.properties[Flags.MAKEPERMANENT].key)
                ) {
                  this.temporaryActionsShow = true
                }
              }
            } else {
              if (!this.isClosed) {
                this.unauthorizedExpiredGeneralPopupShow = true
              }
            }
            // difference of two dates, if validUntilHold date exists and flag extend
            let dateDiff = Date.now() - new Date(this.moc.dates.validUntil)

            if (this.moc.flags.includes('extend') && this.moc.dates.validUntilHold) {
              dateDiff = Date.now() - new Date(this.moc.dates.validUntilHold)
            }

            // days between two dates
            let nDays = dateDiff / (1000 * 3600 * 24)
            let offset = this.moc.changeType.daysInterval ? this.moc.changeType.daysInterval : 0

            if (offset > 0) {
              if (Math.floor(nDays / offset) >= 0 && Math.floor(nDays / offset) < 1) {
                this.$store.dispatch('currentMoc/setFormColor', 'backgroundUnauthorizedExpired')
                this.flagsMessageColor = 'bg-red-lighten-3'
              } else if (Math.floor(nDays / offset) >= 1 && Math.floor(nDays / offset) < 2) {
                this.$store.dispatch('currentMoc/setFormColor', 'backgroundUnauthorizedExpired2')
                this.flagsMessageColor = 'bg-red-accent-4'
              } else if (Math.floor(nDays / offset) >= 2) {
                this.$store.dispatch('currentMoc/setFormColor', 'backgroundUnauthorizedExpired3')
                this.flagsMessageColor = 'bg-purple-accent-4'
              }
            }
          }
        }
      }
      if (this.$refs.mocForm) {
        this.$refs.mocForm.validate()
      }
    },
    processAllFAT: async function () {
      if (!this.isSaving) {
        this.isSaving = true
        this.$refs.loadingDialog.show()
        try {
          // when change Type changed we will need to reprocess all tasks since change types are attached to all tasks
          // we have to store tasks in case a task was satisfied and the task is re-introduced,
          // note: that was the plan, but the plan now is to keep the completed task needed or not
          let holdSatisfiedTasks = this.tasks.filter((t) => {
            return (t.status || {}).value === 'Completed'
          })

          let holdManualAddedTasks = this.tasks.filter((t) => {
            return t.addedVia === AddedVia.MANUAL
          })

          this.tasks = []
          await this.deleteAllTasksForMoc()
          this.loadChangeCategoryTasks()
          this.loadChangeTypeTasks()
          this.loadMocTypeTasks()

          // dedupe tasks
          let concatAndDeDuplicateTasks = (p, ...arrs) => [
            ...new Set(
              [].concat(...arrs).filter((obj, pos, arr) => {
                return arr.map((mapObj) => mapObj[p]).indexOf(obj[p]) === pos
              })
            )
          ]

          // remove satisfied tasks that no longer apply to this moc
          holdSatisfiedTasks = holdSatisfiedTasks.filter((t) =>
            t.addedVia !== AddedVia.QARELATIONSHIP ? this.tasks.findIndex((x) => x.taskId === t.taskId) > -1 : true
          )

          //add back the completed tasks
          this.tasks = concatAndDeDuplicateTasks('taskId', holdSatisfiedTasks, this.tasks)
          
          //remove any task that were manual add we have in holdManualAddedTasks (if the manual task was satisfied)
          this.tasks = this.tasks.filter((t) => !holdManualAddedTasks.includes(t))
          //add the manual tasks back
          this.tasks = concatAndDeDuplicateTasks('taskId', holdManualAddedTasks, this.tasks)

          this.dirtyFat = false
          await this.processFAT()

          //Before we do a think, mark all of the tasks here as loading false
          this.tasks.forEach((i) => {
            i.success = false
            i.loading = false
          })

          this.saveAndContinueClickDialog = false
          this.$refs.loadingDialog.hide()
        } catch (e) {
          this.$refs.loadingDialog.error({ metadata: 'processAllFAT', error: e, message: typeof e === 'string' ? e : e.message  })
        } finally {
          this.isSaving = false
        }
      }
    },
    processQA: async function (advanceType) {
      if (this.moc.answeredQuestions) {
        await QARelationshipService.processQA(this.moc.mocId, this.moc.changeType, this.moc.answeredQuestions)
          .then(async (data) => {
            await this.processQAFat(data).then(async () => await this.saveFormAndCleanUpTasks(advanceType))
          })
          .finally(() => {
            this.questionsDirty = false
          })
      }
    },
    saveDraft: async function () {
      if (this.moc.title || ''.length > 0) {
        if (!this.saveContinueDisabled) {
          // s&c is active so process the form before save as draft.
          await this.saveAndContinueClick()
        }
        let data = this.moc
        if (!this.isSaving) {
          this.$refs.loadingDialog.show()
          MOCService.save(data, AdvanceType.DRAFT)
            .then((result) => {
              if (!result.success) {
                throw new Error(result.message)
              }
            })
            .then(() => {
              this.$refs.loadingDialog.hide({ redirect: true })
            })
            .catch((e) => {
              this.$refs.loadingDialog.error({ metadata: 'saveDraft', error: e, message: this.$t('Error_SaveDraft') })
            })
            .finally(() => {
              this.isSaving = false
            })
        }
      } else {
        this.setFocusOnTitle()
      }
    },
    saveFormAndCleanUpTasks: async function () {
      const data = this.moc
      let aType = AdvanceType.NONE
      await this.saveAndCleanUpTasks().then(
        await MOCService.saveAndContinue(data, aType).then(async (data) => {
          if (data.item1.value.success) {
            this.moc = data.item2
          } else {
            this.dirtyFat = true
            this.$forceUpdate()
          }
        })
      )
    },
    saveAndCleanUpTasks: async function () {
      const data = this.tasks
      await TaskService.saveAndCleanUpTasks(this.moc.mocId, data).then((tasks) => {
        // return all tasks from collection
        this.tasks = tasks
      })
    },
    saveSentBack: async function () {
      if (this.moc.title !== null) {
        if (!this.isSaving) {
          this.isSaving = true
          const data = this.moc
          this.$refs.loadingDialog.show()
          MOCService.save(data, AdvanceType.NONE)
            .then((result) => {
              if (!result.success) {
                throw new Error(result.message)
              }
              this.$refs.loadingDialog.hide({ redirect: true })
            })
            .catch((e) => {
              this.$refs.loadingDialog.error({ metadata: 'saveSentBack', error: e, message: this.$t('Error_SaveSentBack') })
            })
            .finally(() => {
              this.isSaving = false
            })
        }
      } else if (this.moc.title === null) {
        this.setFocusOnTitle()
      }
    },
    silentSave: function () {
      if (!this.isSaving) {
        this.isSaving = true
        const data = this.moc
        MOCService.save(data, AdvanceType.NONE, false)
          .then((result) => {
            if (!result.success) {
              throw new Error(result.message)
            }
          })
          .catch((e) => {
            console.error('error', 'silentSave: ' + e)
          })
          .finally(() => {
            this.isSaving = false
          })
      }
    },
    setApprovalProcessType: function (processType) {
      this.moc.approvalProcessType = processType
    },
    setChangeCategories: function () {
      let sortBy = [{ prop: 'displayOrder', direction: 1 }]

      this.changeCategories = multiPropertySort(
        sortBy,
        this.moc.communityData.changeCategories.filter((cg) => cg.enabled)
      )

      if (this.moc.changeCategory == null) {
        if (this.changeCategories.length === 1) {
          this.moc.changeCategory = this.changeCategories[0].name
          // add facilitator and backup
          if (this.moc.communityData.changeCategories[0].facilitator) {
            this.moc.roles.facilitator = this.moc.communityData.changeCategories[0].facilitator
          }
          if (this.moc.communityData.changeCategories[0].backupFacilitator) {
            this.moc.roles.facilitatorBackup = this.moc.communityData.changeCategories[0].backupFacilitator
          }
          // this is the side effect of having to default to single value
          this.dirtyFat = true
        }
      }
    },
    setChangeType: function () {
      if (this.moc.communityData.changeTypes.length >= 1) {
        let enabledChangeTypes = this.moc.communityData.changeTypes.filter((ct) => {
          return ct.enabled === true
        })

        let sorted = enabledChangeTypes.sort((a, b) => (a.type.value > b.type.value ? 1 : -1))
        this.moc.changeType = sorted[0]
      }
    },
    setFocusOnTitle: function () {
      this.$nextTick(() => this.$refs.title.$refs.title.focus())
    },
    setGroupUsers: function (results) {
      // this is a strange function that works with the results from the userGroupMessageDialog
      switch (results.groupName) {
        case 'fyis':
          this.tempAllRequiredFyis = results.users
          this.moc.roles.fyis = this.tempAllRequiredFyis
          break
        case 'additionalApprovers':
          this.$refs.AdditionalApproversPeoplePicker.removeSelectedUser(results.users)
          break
        case 'owner':
          this.moc.roles.owner = { ...results.users }
          break
      }

      //lets reset stuff
      this.userGroupMessageDialogShow = false
      this.userGroupMessageDialogMessage = ''
      this.userGroupMessageDialogMessageOnly = true
      this.userGroupMessageDialogUser = {}
      this.userGroupMessageDialogChildGroup = []
      this.userGroupMessageDialogParentGroup = []
      this.userGroupMessageDialogKeepBothOnYes = false
    },
    setMocType: function (type) {
      this.moc.mocType = type.value
      // this is a side effect of having to have to default a select to a single value
      this.dirtyFat = true
    },
    populateFormFAT: async function () {
      await this.addRequiredApprovers(this.moc.roles.approvers)
      await this.addRequiredFyis(this.moc.roles.fyis)
      await this.addAdditionalApprovers(this.moc.roles.additionalApprovers)
      await this.addAdditionalFyis(this.moc.roles.additionalFyis)
    },
    onUserUpdated(result) {
      this.viewingUsers = result.users
    },
    loadDisplayNames(communityAbbrev) {
      let displayNames = {}
      this.isOverridingDisplayNames = false

      // check for overrides...
      let overrides = this.$store.state.overrides.communityOverrides
      if (overrides.find((o) => o.communityAbbreviation === communityAbbrev)) {
        // there's an override, just load defaults.
        displayNames = JSON.parse(JSON.stringify(i18next.getResourceBundle('en', 'default')))
        i18next.addResourceBundle('en', communityAbbrev, displayNames, false, true)
        i18next.setDefaultNamespace(communityAbbrev)
        this.isOverridingDisplayNames = true
      } else {
        AdminService.getDisplayNames(communityAbbrev)
          .then((data) => {
            displayNames = data
          })
          .catch((e) => {
            console.log(e)
          })
          .finally(() => {
            i18next.addResourceBundle('en', communityAbbrev, displayNames, false, true)
            i18next.setDefaultNamespace(communityAbbrev)
          })
      }
    },
    browserKilled: function () {
      if (this.id) {
        this.$mocHub.mocClosed(this.id, this.$store.state.currentUser.user.displayName)
      }
    }
  },
  computed: {
    ...mapState('currentUser', ['user']),
    ...mapState('currentMoc', ['mocid', 'mocStatus', 'formColor']),
    ...mapState('currentUserAdminRoles', ['adminRoles']),

    additionalApproversShowRequiredReadOnly: function () {
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.SUBMITTED].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
        case FormStatus.properties[FormStatus.BEINGREVISED].value:
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value: {
          return false
        }
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value: {
          if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
            return false
          }
        }
      }
      return true
    },
    displayMultipassApprovers: function () {
      return this.isMultipass && this.moc.multiPassCurrentPass > 0
    },
    isMultipass: function () {
      return (this.moc.flags || []).includes(Flags.properties[Flags.MULTIPASS].key)
    },
    isMultipassGtZero: function () {
      return this.moc.multiPassCurrentPass > 0
    },
    tasksForCurrentStatusCount() {
      return this.tasksForCurrentStatus.length
    },

    displayHistory() {
      return true
    },
    displaySupportTools() {
      return (
        this.$store.state.currentUserAdminRoles.adminRoles.filter((role) => role === 'ITSupport' || role === 'ProcessManager')
          .length > 0
      )
    },
    displayViewingUsers() {
      return this.viewingUsers.join('; ')
    },
    allTasksComplete: function () {
      return this.tasksComplete
    },
    currentQuestions: function () {
      if (this.moc) {
        if (this.moc.status.value === FormStatus.properties[FormStatus.NEW].value) {
          if ((this.moc.communityData.questions || []).length < 1) {
            return null
          }
          return [...this.moc.communityData.questions].sort((a, b) => a.order - b.order)
        }
        if (this.moc.answeredQuestions) {
          // flatten the question structure in communityData.questions
          const questionValues = (obj) => {
            const array = Array.isArray(obj) ? obj : [obj]

            return array.reduce((acc, value) => {
              acc.push(value)
              if (value && value.values) {
                // look for the sub questions here
                value.values.forEach((vv) => {
                  if (vv.questions) {
                    acc = acc.concat(questionValues(vv.questions))
                  }
                })
                acc = acc.concat(questionValues(value.values))
              }
              return acc
            }, [])
          }

          // console.log(`moc.communityData ${JSON.stringify(this.moc.communityData)}`)
          // console.log(`moc.communityData.questions ${JSON.stringify(this.moc.communityData.questions)}`)
          let flatQuestionValues = questionValues(this.moc.communityData.questions)
          // console.log(`moc.communityData.questions, flatQuestionValues ${JSON.stringify(flatQuestionValues)}`)

          let answersDetails = []

          // console.log(`moc.answeredQuestions ${JSON.stringify(this.moc.answeredQuestions)}`)
          // get the question answer structure from the flattened data for the answered questions
          this.moc.answeredQuestions.forEach((a) => {
            // console.log(`moc.answeredQuestions.forEach ${JSON.stringify(a)}`)          
            
            let found = flatQuestionValues.find((qv) => qv.id === a.id)
            
            if (found) {
              // console.log(`moc.communityData.questions, found ${JSON.stringify(found)}`)
              if (a.parent !== a.id) {
                found.parent = a.parent
              }

              found.answers = a.answers
              found.answers.forEach((aa) => {
                aa.parent = a.parent
              })
              answersDetails.push(found)
            }
          })
          // we are returning temp data to be used at the time of execution
          return answersDetails
        }
      }
      return null
    },
    faBackgroundColor() {
      return !this.readonlyAdditionalApprovers && !this.readonlyAdditionalFyis ? 'backgroundNormal' : 'backgroundReadOnly'
    },
    faComponentColor() {
      return !this.readonlyAdditionalApprovers &&
        !this.readonlyAdditionalFyis &&
        (!this.moc.flags.includes(Flags.properties[Flags.MULTIPASS].key) ||
          (this.moc.flags.includes(Flags.properties[Flags.MULTIPASS].key) && this.moc.multiPassCurrentPass === 0))
        ? 'activePeoplePicker'
        : 'disabledPeoplePicker'
    },
    formColor() {
      return this.$store.state.currentMoc.formColor ? this.$store.state.currentMoc.formColor : 'white'
    },
    isDirtyFat: function () {
      return this.dirtyFat
    },
    isQuestionsDirty: function () {
      return this.questionsDirty
    },
    showTaskLinkOnly: function () {
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
        case FormStatus.properties[FormStatus.SUBMITTED].value:
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value:
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value:
        case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value:
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value: {
          return false
        }
        default:
          return true
      }
    },
    beingRevisedStatus: function () {
      return FormStatus.properties[FormStatus.BEINGREVISED].value
    },
    sentBackStatus: function () {
      return FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value
    },
    changeIsInPlaceWithConsequentialActionsRequired: function () {
      return FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value
    },
    temporaryChangeIsInPlaceWithConsequentialActionsRequired: function () {
      return FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value
    },
    closedVoidedStatus: function () {
      return FormStatus.properties[FormStatus.CLOSEDVOIDED].value
    },
    closedStrandedStatus: function () {
      return FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
    },
    submittedStatus: function () {
      return FormStatus.properties[FormStatus.SUBMITTED].value
    },
    availableForDecisionStatus: function () {
      return FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value
    },
    newStatus: function () {
      return FormStatus.properties[FormStatus.NEW].value
    },
    draftStatus: function () {
      return FormStatus.properties[FormStatus.DRAFT].value
    },
    pendingOwnerDecisionStatus: function () {
      return FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value
    },
    lastSentBackHistoryItem: function () {
      if (this.moc.sentBackHistory) {
        return this.moc.sentBackHistory[this.moc.sentBackHistory.length - 1]
      }
      return null
    },
    displayApprovals: function () {
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value ===
          FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.OWNERAPPROVEDDEFERREDSTARTDATE].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        (this.moc.flags || []).includes(Flags.properties[Flags.EXTEND].key) ||
        (this.moc.flags || []).includes(Flags.properties[Flags.MAKEPERMANENT].key) ||
        (this.moc.flags || []).includes(Flags.properties[Flags.MULTIPASS].key)
      ) {
        return true
      }
      return false
    },
    displayFacilitatorActions: function () {
      if (
        ((this.moc.status.value === FormStatus.properties[FormStatus.NEW].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value) &&
          this.moc.roles.facilitator.displayName) ||
        this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value
      ) {
        return true
      }

      return false
    },
    displaySentBackReadOnly: function () {
      if (this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value) {
        return true
      }
      return false
    },
    showTasks: function () {
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      ) {
        return false
      }

      return true
    },
    fullValid: {
      cache: false,
      get: function () {
        this.areQuestionsCompleted()
        this.areTasksCompleted()

        if (this.saveContinueDisabled) {
          if (this.questionsComplete && this.tasksComplete && this.valid) {
            return true
          }
        }
        return false
      }
    },
    saveEnabled: {
      cache: false,
      get: function () {
        if (this.saveContinueDisabled) {
          return true
        }

        return false
      }
    },
    initiatorRules: {
      get: function () {
        return [(v) => !!v || this.$t('Error_InitiatorRequired')]
      }
    },
    displayCoordinatorActions: function () {
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value &&
        this.moc.flags &&
        this.moc.flags.includes(Flags.properties[Flags.UNAUTHORIZED].key) &&
        this.moc.flags.includes(Flags.properties[Flags.MAKEPERMANENT].key) &&
        this.moc.changeType.type.key === ChangeTypes.properties[ChangeTypes.TEMPORARY].key
      ) {
        if (this.myRoles.includes(Roles.properties[Roles.COORDINATOR].value)) {
          return true
        }
      }

      if (
        this.moc.status.value ===
          FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value &&
        this.moc.flags.includes(Flags.properties[Flags.RETURNTOORIGINALCONDITION].key)
      ) {
        if (this.myRoles.includes(Roles.properties[Roles.COORDINATOR].value)) {
          return true
        }
      }

      if (
        this.moc.flags &&
        (this.moc.flags.includes(Flags.properties[Flags.UNAUTHORIZED].key) ||
          this.moc.flags.includes(Flags.properties[Flags.OWNEREDITABLEMAKEPERMANENT].key))
      ) {
        return false
      }

      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value
      ) {
        if (this.myRoles.includes(Roles.properties[Roles.COORDINATOR].value)) {
          return true
        }
      }
      return false
    },
    displayOwnerActions: function () {
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.BEINGREVISED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.OWNERAPPROVEDDEFERREDSTARTDATE].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value
      ) {
        if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
          return true
        }
      }

      if (
        this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value &&
        (this.moc.flags || []).includes(Flags.properties[Flags.MULTIPASS].key) &&
        this.myRoles.includes(Roles.properties[Roles.OWNER].value)
      ) {
        return true
      }
      return false
    },
    displayInitiatorActions: function () {
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.BEINGREVISED].value
      ) {
        if (
          this.myRoles.includes(Roles.properties[Roles.INITIATOR].value) ||
          this.myRoles.includes(Roles.properties[Roles.COINITIATOR].value)
        ) {
          return true
        }
      }
      return false
    },
    displayClosedVoided: function () {
      if ((this.moc.flags || []).includes('extend')) {
        return false
      }
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.NEW].value
      ) {
        return false
      }
      if (
        (this.myRoles.includes(Roles.properties[Roles.INITIATOR].value) ||
          this.myRoles.includes(Roles.properties[Roles.COINITIATOR].value)) &&
        (this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value)
      ) {
        return true
      }
      if (
        (this.myRoles.includes(Roles.properties[Roles.FACILITATOR].value) ||
          this.myRoles.includes(Roles.properties[Roles.FACILITATORBACKUP].value)) &&
        this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value
      ) {
        return true
      }
      return false
    },
    isBeingRevised: function () {
      return this.moc.status.value === FormStatus.properties[FormStatus.BEINGREVISED].value
    },
    isInitiator: function () {
      if (
        (this.myRoles.includes(Roles.properties[Roles.INITIATOR].value) ||
          this.myRoles.includes(Roles.properties[Roles.COINITIATOR].value)) &&
        (this.moc.status.value === FormStatus.properties[FormStatus.NEW].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.BEINGREVISED].value)
      ) {
        return true
      }
      return false
    },
    isSentBack: function () {
      return this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value
    },
    isClosed: function () {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      )
    },
    isDraft: function () {
      return this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value
    },
    isNew: function () {
      return this.moc.status.value === FormStatus.properties[FormStatus.NEW].value
    },
    isOwnerApprovedDeferredStartDate: function () {
      return this.moc.status.value === FormStatus.properties[FormStatus.OWNERAPPROVEDDEFERREDSTARTDATE].value
    },
    isAvailableForDecision: function () {
      return this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value
    },
    requiredApprovers: function () {
      return this.moc.roles.approvers
    },
    initiator: function () {
      if (this.moc.roles.initiator) {
        if ((this.moc.roles.initiator || {}).displayName == null) {
          return this.$store.state.currentUser.user
        } else {
          return this.moc.roles.initiator
        }
      }
      return null
    },
    allowEdit: function () {
      if (
        (this.moc.roles.initiator &&
          this.moc.roles.initiator.id === this.user.id &&
          (this.moc.status.value === FormStatus.properties[FormStatus.NEW].value ||
            this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value)) ||
        (this.moc.roles.coinitiator != null &&
          this.moc.roles.coinitiator.id === this.user.id &&
          (this.moc.status.value === FormStatus.properties[FormStatus.NEW].value ||
            this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value))
      ) {
        return true
      } else {
        return false
      }
    },
    readonlyAdditionalApprovers: function () {
      if (this.moc) {
        switch (this.moc.status.value) {
          case FormStatus.properties[FormStatus.NEW].value:
            return false
          case FormStatus.properties[FormStatus.DRAFT].value:
          case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
            if (
              this.myRoles.includes(Roles.properties[Roles.INITIATOR].value) ||
              this.myRoles.includes(Roles.properties[Roles.COINITIATOR].value)
            ) {
              return false
            }
            return true
          case FormStatus.properties[FormStatus.SUBMITTED].value:
            if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
              if (this.moc.flags && this.moc.flags.includes(Flags.properties[Flags.MAKEPERMANENT].key)) {
                return false
              }
            }

            if (
              this.myRoles.includes(Roles.properties[Roles.FACILITATOR].value) ||
              this.myRoles.includes(Roles.properties[Roles.FACILITATORBACKUP].value)
            ) {
              return false
            }
            return true
          case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value: {
            if (
              this.myRoles.includes(Roles.properties[Roles.OWNER].value) ||
              this.myRoles.includes(Roles.properties[Roles.FACILITATOR].value) ||
              this.myRoles.includes(Roles.properties[Roles.FACILITATORBACKUP].value)
            ) {
              return false
            }
            return true
          }
          case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value: {
            return true
          }
          case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value: {
            if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
              return false
            }
            return true
          }
          case FormStatus.properties[FormStatus.BEINGREVISED].value: {
            if (this.moc.history.some((e) => e.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value)) {
              return true
            }

            if (
              this.previousFormStatus.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value &&
              (this.myRoles.includes(Roles.properties[Roles.INITIATOR].value) ||
                this.myRoles.includes(Roles.properties[Roles.COINITIATOR].value) ||
                this.myRoles.includes(Roles.properties[Roles.OWNER].value))
            ) {
              return false
            }

            if (
              (this.previousFormStatus.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
                this.previousFormStatus.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value) &&
              (this.myRoles.includes(Roles.properties[Roles.INITIATOR].value) ||
                this.myRoles.includes(Roles.properties[Roles.COINITIATOR].value) ||
                this.myRoles.includes(Roles.properties[Roles.OWNER].value))
            ) {
              return false
            }
            return true
          }
          case FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
            if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
              if (this.moc.flags && this.moc.flags.includes(Flags.properties[Flags.OWNEREDITABLEMAKEPERMANENT].key)) {
                return false
              }
            }
            return true
          default:
            return true
        }
      }
      return true
    },
    readonlyAdditionalFyis: function () {
      if (this.moc) {
        switch (this.moc.status.value) {
          case FormStatus.properties[FormStatus.NEW].value:
            return false
          case FormStatus.properties[FormStatus.DRAFT].value:
          case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
            if (
              this.myRoles.includes(Roles.properties[Roles.INITIATOR].value) ||
              this.myRoles.includes(Roles.properties[Roles.COINITIATOR].value)
            ) {
              return false
            }
            return true
          case FormStatus.properties[FormStatus.SUBMITTED].value:
            if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
              if (this.moc.flags && this.moc.flags.includes(Flags.properties[Flags.MAKEPERMANENT].key)) {
                return false
              }
            }

            if (
              this.myRoles.includes(Roles.properties[Roles.FACILITATOR].value) ||
              this.myRoles.includes(Roles.properties[Roles.FACILITATORBACKUP].value)
            ) {
              return false
            }
            return true
          case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value: {
            if (
              this.myRoles.includes(Roles.properties[Roles.OWNER].value) ||
              this.myRoles.includes(Roles.properties[Roles.FACILITATOR].value) ||
              this.myRoles.includes(Roles.properties[Roles.FACILITATORBACKUP].value)
            ) {
              return false
            }
            return true
          }
          case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value: {
            return true
          }
          case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value: {
            if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
              return false
            }
            return true
          }
          case FormStatus.properties[FormStatus.BEINGREVISED].value: {
            if (this.moc.history.some((e) => e.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value)) {
              return true
            }

            if (
              this.previousFormStatus.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value &&
              (this.myRoles.includes(Roles.properties[Roles.COORDINATOR].value) ||
                this.myRoles.includes(Roles.properties[Roles.OWNER].value))
            ) {
              return false
            }

            if (
              (this.previousFormStatus.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
                this.previousFormStatus.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value) &&
              (this.myRoles.includes(Roles.properties[Roles.INITIATOR].value) ||
                this.myRoles.includes(Roles.properties[Roles.COINITIATOR].value) ||
                this.myRoles.includes(Roles.properties[Roles.OWNER].value))
            ) {
              return false
            }
            return true
          }
          case FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
            if (this.myRoles.includes(Roles.properties[Roles.OWNER].value)) {
              if (this.moc.flags && this.moc.flags.includes(Flags.properties[Flags.OWNEREDITABLEMAKEPERMANENT].key)) {
                return false
              }
            }
            return true
          default:
            return true
        }
      }
      return true
    },
    readonly: function () {
      if (this.moc) {
        switch (this.moc.status.value) {
          case FormStatus.properties[FormStatus.NEW].value:
          case FormStatus.properties[FormStatus.DRAFT].value:
          case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
            if (
              (this.moc.roles.initiator && this.moc.roles.initiator.id === this.user.id) ||
              (this.moc.roles.coinitiator != null && this.moc.roles.coinitiator.id === this.user.id)
            ) {
              return false
            }
            break
          case FormStatus.properties[FormStatus.BEINGREVISED].value:
            if (this.moc.history.some((e) => e.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value)) {
              return true
            }

            if (
              (this.previousFormStatus.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
                this.previousFormStatus.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value) &&
              ((this.moc.roles.initiator && this.moc.roles.initiator.id === this.user.id) ||
                (this.moc.roles.coinitiator != null && this.moc.roles.coinitiator.id === this.user.id) ||
                (this.moc.roles.owner != null && this.moc.roles.owner.id === this.user.id))
            ) {
              return false
            }
            break
          default:
            return true
        }
        return true
      } else {
        return true
      }
    },
    mocTypes: function () {
      if (this.moc) {
        if (this.moc.changeCategory) {
          const changeCat = this.moc.changeCategory
          const index = this.$_.findIndex(this.moc.communityData.changeCategories, function (o) {
            return o.name === changeCat
          })

          if (index >= 0) {
            // if there is only one mocType for this change cat, default to it
            const disabledMocTypes = this.moc.communityData.mocTypes.filter((m) => !m.enabled).map((m) => m.id)
            const enabledChangeCategoryMocTypes = this.moc.communityData.changeCategories[index].mocTypes.filter(
              (m) => !disabledMocTypes.includes(m.id)
            )
            // if (enabledChangeCategoryMocTypes.length === 1 && this.moc.mocType === '') {
            if (enabledChangeCategoryMocTypes.length === 1 && this.moc.mocType === null) {
              this.setMocType(enabledChangeCategoryMocTypes[0])
              this.loadMocTypeTasks()
              this.addRequiredApprovers(enabledChangeCategoryMocTypes[0].approvers)
              this.addRequiredFyis(enabledChangeCategoryMocTypes[0].fyis)
            }

            return enabledChangeCategoryMocTypes
          } else {
            return []
          }
        } else {
          return []
        }
      } else {
        return []
      }
    },
    approvalProcessTypes: function () {
      if (this.moc) {
        if (this.moc.changeCategory) {
          const changeCat = this.moc.changeCategory
          const index = this.$_.findIndex(this.moc.communityData.changeCategories, function (o) {
            return o.name === changeCat
          })
          if (index >= 0) {
            let sortBy = [{ prop: 'displayOrder', direction: 1 }]
            const ordered = multiPropertySort(sortBy, this.moc.communityData.changeCategories[index].approvalProcessTypes)

            // if we only have one APT then default to it
            if (ordered.length === 1 && this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value) {
              if (!(this.moc.approvalProcessType || {}).approvalType) {
                this.setApprovalProcessType(ordered[0])
                this.approvalProcessTypeChanged(this.moc.approvalProcessType)
                if (!this.moc.roles.owner) {
                  this.setOwner(ordered[0].owner)
                }
              }
            }
            return ordered
          } else {
            return null
          }
        } else {
          return null
        }
      } else {
        return null
      }
    },
    changeTypes: function () {
      if (this.moc.communityData.changeTypes) {
        let enabledChangeTypes = this.moc.communityData.changeTypes.filter((ct) => {
          return ct.enabled === true
        })

        let sorted = enabledChangeTypes.sort((a, b) => (a.type.value > b.type.value ? 1 : -1))

        return sorted
      } else {
        return []
      }
    },
    communityName: function () {
      return this.moc.communityName || this.moc.communityData.communityName
    },
    tasksFilter: function () {
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value: {
          let filter = { taskListType: { key: `${TaskListTypes.SUBMIT.toString()}` } }
          return filter
        }
        case FormStatus.properties[FormStatus.SUBMITTED].value: {
          let filter = { taskListType: { key: `${TaskListTypes.SENDFORDECISION.toString()}` } }
          return filter
        }
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value: {
          let filter = { taskListType: { key: `${TaskListTypes.ENABLEOWNERDECISIONTOOCCUR.toString()}` } }
          return filter
        }
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value: {
          let filter = { taskListType: { key: `${TaskListTypes.PROCEEDWITHIMPLEMENTATION.toString()}` } }
          return filter
        }
        case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value: {
          let filter = { taskListType: { key: `${TaskListTypes.CONFIRMCHANGEISINPLACE.toString()}` } }
          return filter
        }
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value: {
          let filter = { taskListType: { key: `${TaskListTypes.CONFIRMCHANGEISFULLYIMPLEMENTED.toString()}` } }
          return filter
        }
        default:
          return null
      }
    },
    formReady: function () {
      if (this.moc && this.myRoles && this.moc.mocId) {
        return true
      }
      return false
    },
    sentBackReason: function () {
      return this.moc.sentBackReason
    },
    getOwner: function () {
      if (this.moc.roles.owner) {
        return this.moc.roles.owner
      }
      return null
    },
    currentTasks: function () {
      if (this.tasks) {
        return this.tasks
      }
      return null
    },

    selectedChangeTypeKey: function () {
      if (this.moc.changeType.type) {
        return this.moc.changeType.type.key
      }
      return null
    },
    selectedChangeType: function () {
      if (this.moc.changeType.type) {
        return this.moc.changeType.type
      }
      return null
    },
    temporaryChangeTypeKey: function () {
      return ChangeTypes.TEMPORARY.toString()
    },
    loading: function () {
      return !this.loaded
    },
    newFormQuestions: function () {
      return this.moc.communityData.questions
    },
    readonlyDiscussion() {
      let hasRolesInMoc = false
      Object.keys(this.moc.roles).forEach((key) => {
        if (hasRolesInMoc) return
        const currentRole = this.moc.roles[key]
        if (!currentRole) return
        if (Array.isArray(currentRole)) {
          const index = currentRole.findIndex((c) => c.id === this.$store.state.currentUser.user.id)
          hasRolesInMoc = index > -1
        } else {
          hasRolesInMoc = currentRole.id === this.$store.state.currentUser.user.id
        }
      })

      if (this.moc.multiPassCurrentPass > 0 && !hasRolesInMoc) {
        const pass = this.moc.approvalProcessType.multiPass.approvalPasses[this.moc.multiPassCurrentPass - 1]
        const passApprovers = (pass.approvers || []).concat(pass.additionalApprovers || [])
        passApprovers.forEach((a) => {
          if (hasRolesInMoc) return
          hasRolesInMoc = a.id === this.$store.state.currentUser.user.id
        })
      }
      return !(
        hasRolesInMoc &&
        (this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value)
      )
    },
    displayDiscussion() {
      return this.moc.dates.availableForDecision
    },
    initialDiscussionDisplay() {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value
      )
    },
    saveContinueDisabled: function () {
      // for communities that do not have questions
      if ((this.currentQuestions || []).length === 0) {
        if (this.isDirtyFat && this.valid) {
          return false
        } else {
          return true
        }
      }

      if (!this.isDirtyFat && (this.currentQuestions || []).length > 0 && !this.isQuestionsDirty && this.valid) {
        return true
      }

      if (!this.isDirtyFat && (this.currentQuestions || []).length > 0 && this.isQuestionsDirty && this.valid) {
        return false
      }

      if (this.isDirtyFat && (this.currentQuestions || []).length > 0 && !this.isQuestionsDirty && this.valid) {
        return false
      }

      if (!this.isDirtyFat && !this.valid) {
        return true
      }
      return false
    },
    getMocStartDate() {
      console.log(`this.moc.dates.start ${this.moc.dates.start}`)
      return new Date(this.moc.dates.start)
    }
  },
  async created() {
    this.formLoaded = false
    if (this.$route.params.shortName) {
      // get our displayNames
      this.loadDisplayNames(this.$route.params.shortName)
      this.$store.dispatch('currentMoc/setFormColor', 'backgroundNormal')
      AdminService.isCommunityConfidential(this.$route.params.shortName)
        .then((result) => {
          this.isCommunityConfidential = result
        })
        .catch((e) => {
          console.error('isCommunityConfidential', e)
        })
        .catch(() => {})
      AdminService.isOpenTextEnabled(this.$route.params.shortName)
        .then((result) => {
          this.openTextEnabled = result === true
        })
        .catch((e) => {
          console.error('isOpenTextEnabled', e)
        })
      console.log(`moc props id ${this.id}`)
      console.log(`this.$route.params ${JSON.stringify(this.$route.params)}`)
      // console.log(`this.route.params ${JSON.stringify(this.route.params)}`)
      if (this.id) {
        await this.fetchMoc(this.$route.params.shortName, this.id)
          .then(async () => {
            await this.loadForm()
            this.$store.dispatch('currentMoc/setId', this.id)
            this.$store.dispatch('currentMoc/setStatus', this.moc.status)
          })
          .catch((e) => {
            this.$refs.loadingDialog.error({ redirect: true, metadata: 'created: fetch MOC', error: e })
          })

        if (this.id) {
          this.$mocHub.mocOpened(this.id, this.$store.state.currentUser.user.displayName)
          // this.$mocHub.$on('user-updated', this.onUserUpdated)
          EventBus.on('user-updated', this.onUserUpdated)
        }

        window.addEventListener('beforeunload', this.browserKilled)
      } else {
        this.createNewMoc()
      }
    } else {
      //   // you don't belong here
    }
    this.formLoaded = true
  },
  beforeDestroy() {
    if (this.id) {
      this.$mocHub.mocClosed(this.id, this.$store.state.currentUser.user.displayName)
    }
  },
  mounted() {
    if (this.isOverridingDisplayNames) this.$refs.toaster.pop('success', 'Overriding Display Names')
  },
  provide() {
    return { getPeoplePickerInjectedProperties: this.getPeoplePickerInjectedProperties }
  }
}
</script>

<style scoped>
.viewers {
  color: gray;
  text-align: right;
  display: block;
  font-size: x-small;
}
.revision {
  color: gray;
  text-align: right;
  display: block;
}
.dialog-gray-text {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}
</style>

<style>
.moc-form .v-text-field.v-input--is-readonly > .v-input__control > .v-input__slot:after {
  border-style: none !important;
}

.moc-form .v-text-field.v-input--is-readonly > .v-input__control .theme--light.v-label {
  color: rgba(0, 0, 0, 0.6) !important;
}
</style>
